/* ============================================================================
   MAIN — nơi duy nhất nối các mảnh lại: content → state → store → render/UI.

   Luồng dữ liệu đúng MỘT chiều:

       input ─▶ dispatch(action) ─▶ reduce ─▶ state mới ─▶ render + HUD
                                              │
                                              └─▶ save (snapshot)

   Không có đường tắt nào từ UI sửa thẳng state. Muốn thêm cơ chế mới thì thêm
   Action, không phải thêm một biến toàn cục.

   Những thứ KHÔNG phải state (và cố tình không phải): settings của máy, hạt
   hiệu ứng, rung, thẻ "Ngày N", tutorial, ô đang ngắm, đích đang đi tới. Chúng
   là ý định nhất thời hoặc sở thích của thiết bị — không đáng nằm trong save.
============================================================================ */

import "./style.css";

import { ART, buildAtlas } from "./art/atlas.ts";
import { createInput, bindTouchButton } from "./core/input.ts";
import { observeScreen } from "./core/screen.ts";
import { createNavigator } from "./core/navigate.ts";
import { applySettings, loadSettings, saveSettings, type Settings } from "./core/settings.ts";
import { buzz, setHaptics, setPadRumble } from "./core/haptics.ts";
import { MAX_CONSECUTIVE_ERRORS, createLoop } from "./core/loop.ts";
import { createStore, type Store } from "./core/store.ts";
import { CORE_VERSION } from "./core/version.ts";
import {
  SLOT_BACKUP,
  backupSave,
  exportToFile,
  importFromFile,
  loadSlot,
  migrateSaveEx,
  saveGame,
} from "./core/save.ts";
import { adoptState as napState } from "./game/adopt.ts";
import {
  checkForUpdate,
  pendingContentVersion,
  resolveContent,
  revertToBundled,
} from "./core/content/ota.ts";
import { initAudio, isMuted, play, setMuted } from "./core/sfx.ts";
import { createCamera } from "./render/camera.ts";
import { createRenderer, type BurstKind, type Cursor } from "./render/draw.ts";
import { createHud } from "./ui/hud.ts";
import { createMenus } from "./ui/menus.ts";
import { createToasts } from "./ui/toast.ts";
import { createMinimap } from "./ui/minimap.ts";
import { createDevPanel } from "./ui/devpanel.ts";
import { padButtonName } from "./core/gamepad.ts";
import { timChoNgoi, type Seat } from "./ui/focus.ts";
import { createBuildMode } from "./ui/buildmode.ts";
import { createTutorial, DESKTOP_STEPS, PAD_STEPS, TOUCH_STEPS } from "./ui/tutorial.ts";
import type { Content, GameState, InteractKind, SaveData, Stats } from "./game/types.ts";
import { createNewGame } from "./game/state.ts";
import { canCraft, canUseAt, interactAt, linePath, missingFor } from "./game/actions.ts";
import { autoJob, autoStopReason, aimStillValid, facingTile, hintOf, infoHint, nextTarget, pressPlan, reachTargets, tileInfo, type Hint, type Press,
  ghiNhoNgam,
} from "./game/hint.ts";
import { forecastDef, weatherDef, isOutdoor } from "./game/weather.ts";
import { seasonIndex, currentSeason } from "./game/season.ts";
import { animalStats } from "./game/animals.ts";
import { itemName } from "./game/items.ts";
import { workerCard } from "./game/workers.ts";
/* `TILE` lấy từ TẦNG LUẬT CHƠI, không phải từ atlas.

   Trước Đợt 24 chỗ này import `TILE` từ `art/atlas.ts` rồi dùng nó làm đơn vị
   THẾ GIỚI: `camera.setWorld(w * TILE)`, `player.x / TILE`, `minimap.setView`.
   Hai vai trùng giá trị nên không ai thấy gì — cho tới ngày cỡ ẢNH đổi mà đơn
   vị thế giới thì không, tức đúng đợt này. Toạ độ nằm trong bản lưu, nên nguồn
   duy nhất của nó phải là `game/world.ts`. */
import { TILE, canPlaceBuilding, inReach } from "./game/world.ts";
import type { UseKind } from "./game/actions.ts";

/** Gốc URL phục vụ content OTA. Để trống ("") = tắt hẳn, game chạy thuần offline. */
const CONTENT_URL = "https://oni-farm.pages.dev";

/* Ngân sách MỖI LẦN GỌI MẠNG của cổng khởi động.

   Không phải ngân sách tổng: một pack có mười mấy file thì tệ nhất là mười mấy
   lần chờ. Chấp nhận, vì lúc ấy màn hình đang ĐẾM "3/14" — người chơi thấy nó
   đang tiến, và đó là một sự chờ khác hẳn với một màn hình đứng im. Còn nhánh
   thường ngày (đã là bản mới nhất) chỉ tốn ĐÚNG MỘT lần gọi: latest.json. */
const OTA_BOOT_MS = 2500;

/* Tầm với: chỉ có MỘT luật, `inReach()`/`inInteractRange()` trong game/world.ts,
   và từ Đợt 21 mọi cú bấm đi qua `pressPlan` nên UI không còn con số riêng
   nào nữa. (Từng có một hằng 1,8 ô ở đây — lệch 0,2 ô với luật thật đã đẻ ra
   một lỗi rất khó thấy: nút báo "làm được", reducer từ chối trong im lặng.) */

const $ = <T extends HTMLElement>(sel: string) => document.querySelector(sel) as T;

/** Sự kiện cài PWA của Chrome/Edge — bắt sớm, dùng sau ở menu Tạm dừng. */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}
let installPrompt: BeforeInstallPromptEvent | null = null;
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  installPrompt = e as BeforeInstallPromptEvent;
});

/* ---- Chặn zoom của trình duyệt trên trang game -------------------------
   `user-scalable=no` bị iOS bỏ qua từ lâu; thứ iOS tôn trọng là
   `touch-action: manipulation` (CSS) cho chạm kép, còn véo hai ngón thì phải
   chặn bằng JS. Ba lớp bảo hiểm: gesturestart (Safari), touchmove nhiều ngón
   (Chrome/Android), và chạm kép trên phần tử KHÔNG phải nút/ô — nút thì để
   nguyên, nếu không bấm nhanh hai lần vào +/− sẽ mất một lần.

   Đợt 27 gỡ chạm-kép-để-làm của GAME, nhưng khối này KHÔNG đụng tới: nó chặn
   zoom của TRÌNH DUYỆT, một chuyện khác hẳn. Gỡ nó đi thì chạm nhanh hai lần
   trên canvas sẽ phóng to cả trang trên iOS. */
function preventBrowserZoom() {
  const opts = { passive: false } as AddEventListenerOptions;
  document.addEventListener("gesturestart", (e) => e.preventDefault(), opts);
  document.addEventListener("gesturechange", (e) => e.preventDefault(), opts);
  document.addEventListener(
    "touchmove",
    (e) => {
      if (e.touches.length > 1) e.preventDefault();
    },
    opts,
  );
  let lastEnd = 0;
  document.addEventListener(
    "touchend",
    (e) => {
      const now = performance.now();
      const interactive = (e.target as HTMLElement | null)?.closest?.(
        "button, .slot, .bslot, .tab, .switch, .segment, a, input, [role=button]",
      );
      if (now - lastEnd < 320 && !interactive) e.preventDefault();
      lastEnd = now;
    },
    opts,
  );
  document.addEventListener("dblclick", (e) => e.preventDefault(), opts);
  // Ctrl + lăn chuột / Ctrl +- trên desktop: giữ nguyên tỉ lệ pixel art.
  document.addEventListener(
    "wheel",
    (e) => {
      if (e.ctrlKey) e.preventDefault();
    },
    opts,
  );
  document.addEventListener("keydown", (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === "+" || e.key === "-" || e.key === "=" || e.key === "0"))
      e.preventDefault();
  });
}
preventBrowserZoom();

async function boot() {
  const bootEl = $("#boot");
  const showError = (msg: string, tieuDe = "Không khởi động được. Nội dung game có vấn đề:") => {
    bootEl.innerHTML = `<div class="inner"><div class="logo">ONI<span>FARM</span></div>
      <p></p>
      <div class="err"></div></div>`;
    (bootEl.querySelector("p") as HTMLElement).textContent = tieuDe;
    (bootEl.querySelector(".err") as HTMLElement).textContent = msg;
    // Vào game rồi thì màn chờ đã đeo `.done` (mờ hẳn, không nhận chạm) — phải
    // cởi ra, nếu không bảng lỗi hiện mà không ai thấy.
    bootEl.classList.remove("done");
    bootEl.style.display = "grid";
  };

  /**
   * MÀN CHẶN: save trên máy này của một bản game MỚI HƠN.
   *
   * Không tạo store, không đăng ký autosave, không chạy vòng lặp — tức là
   * KHÔNG CÓ ĐƯỜNG NÀO ghi vào khoá save. Trước đây chỗ này bắt đầu nông trại
   * mới, rồi autosave 30 giây đè lên chính cái save còn nguyên vẹn đó.
   */
  const chanSaveMoiHon = () => {
    bootEl.innerHTML = `<div class="inner"><div class="logo">ONI<span>FARM</span></div>
      <p>Bản lưu trên máy này được ghi bởi một <b>phiên bản game mới hơn</b> bản đang chạy.
      Game không mở nó để khỏi làm hỏng. Bấm cập nhật rồi mở lại — tiến trình vẫn còn nguyên.</p>
      <button type="button" class="capnhat">Cập nhật ngay</button></div>`;
    bootEl.querySelector(".capnhat")!.addEventListener("click", () => void buocCapNhat());
    bootEl.classList.remove("done");
    bootEl.style.display = "grid";
  };

  let pendingVersion: string | null = null;
  /** Đã nhận tay cầm ở khung trước chưa — để chỉ báo MỘT lần lúc vừa cắm. */
  let padOn = false;
  /** Tấm sheet đang giữ tiêu điểm. So bằng THAM CHIẾU phần tử, không bằng cờ
   *  đóng/mở: mỗi màn con (Cài đặt, Kho, Cập nhật…) dựng một `.modal` MỚI mà
   *  `menus.isOpen()` vẫn true suốt, nên một cái cờ sẽ chỉ đặt tiêu điểm cho
   *  màn đầu tiên rồi thôi — và mọi màn con đều mở ra với tiêu điểm nằm trên
   *  `<body>`, tức là D-pad phải bấm một cái vô ích mới bắt đầu chạy. */
  let focusedRootEl: HTMLElement | null = null;
  /**
   * Dấu vết của khung hình TRƯỚC, để nhận lại sau khi menu tự dựng lại.
   *
   * Ghi mỗi khung hình, nên nó luôn là ảnh chụp ngay trước cú bấm đã gây ra
   * lần dựng lại đó. Ba thứ cùng chết khi `shell()` xoá `root`: phần tử đang
   * đeo vòng vàng, chỗ cuộn của danh sách, và cả hoạt cảnh mở sheet.
   */
  interface FocusMemo {
    /** định danh MÀN HÌNH (tiêu đề), không phải phần tử */
    screen: string;
    /** tab đang bật — đổi tab là đổi hẳn nội dung, không khôi phục chỗ ngồi */
    tab: string;
    scroll: number;
    /** chỗ ngồi của phần tử đang có tiêu điểm; null khi không có gì đeo tiêu
     *  điểm (chạm trên iOS không tạo tiêu điểm — lúc đó vẫn khôi phục chỗ cuộn) */
    at: Seat | null;
  }
  let focusMemo: FocusMemo | null = null;
  /** Đã xem sơ đồ nút tay cầm trên máy này chưa. */
  const PAD_SEEN = "oni-farm:pad-help-seen";

  /* Câu đang in trên màn khởi động. Màn này xưa nay chỉ nói một câu duy nhất
     ("Đang dựng nông trại…") rồi biến mất, nên mọi việc xảy ra sau nó — hỏi
     phiên bản, tải nội dung, đối chiếu — đều vô hình. Cường: "sao lúc vào game
     không có cập nhật ở trang khởi tạo nông trại luôn: đang kiểm tra phiên
     bản, đang tải dữ liệu, đang đối chiếu... xong hết thì vô chơi thôi". */
  const bootSay = (cau: string) => {
    const p = bootEl.querySelector("p");
    if (p) p.textContent = cau;
  };

  /* ---- 1. content: pack OTA đã cache, nếu không thì pack đóng kèm ---- */
  /* `!` vì trình biên dịch không nhìn thấy được rằng `napNoiDung()` LUÔN gán
     `content` trước khi ai đó đọc tới — nó gán từ trong một closure. */
  let content!: Content;
  let contentSource: "bundled" | "ota" = "bundled";
  const contentWarnings: string[] = [];
  const napNoiDung = async () => {
    const r = await resolveContent();
    content = r.content;
    contentSource = r.source;
    contentWarnings.push(...r.warnings);
  };
  try {
    await napNoiDung();
  } catch (e) {
    showError(e instanceof Error ? e.message : String(e));
    return;
  }

  /* ---- 1a. MÃ GAME: bản mới đã tải xong từ phiên trước thì NHẬN NGAY ----

     `registerType: "prompt"` cố ý không cho service worker mới tự chiếm quyền
     GIỮA VÁN — đúng, vì tải lại trang lúc đang chơi là mất phần chưa lưu.
     Nhưng ở ĐÂY thì chưa có ván nào: chưa store, chưa autosave, không có gì để
     mất. Vậy mà bản trước vẫn bắt chờ một cú bấm vào thanh nhỏ ở đáy màn hình
     — thanh ấy hiện lúc người chơi đang nhìn chỗ khác, nên người mở PWA suốt
     ngày ở lại bản cũ vô thời hạn. Đó là nửa còn lại của "sao cập nhật lâu".

     Chỉ nhận bản ĐÃ TẢI XONG và đang xếp hàng (`reg.waiting`) — không đứng đợi
     một bản đang tải, vì cái đó thì đúng là chờ mạng thật. Bản đang tải vẫn về
     đích ngầm và sẽ được nhận ở lần mở sau. */
  if (await nhanBanMaMoi(bootSay)) return;

  /* ---- 1b. CỔNG KHỞI ĐỘNG: hỏi bản mới NGAY BÂY GIỜ, không để lần sau ----

     Đây là chỗ AN TOÀN duy nhất để đổi luật chơi: chưa có store, chưa có
     autosave, chưa có ván nào để làm hỏng. Bản trước hỏi thăm ở CUỐI đường
     khởi động, sau khi mọi thứ đã dựng xong bằng content cũ — nên pack tải về
     phải chờ tới lần mở game sau mới có hiệu lực. Muốn thấy một thay đổi nội
     dung, người chơi phải mở game hai lần, và lần thứ nhất không nói gì cả.

     Nguyên tắc 1 của ota.ts vẫn giữ nguyên: KHÔNG BAO GIỜ CHẶN. Mạng chậm,
     máy chủ chết, đang offline — mọi nhánh đều rơi vào cùng một chỗ: chơi tiếp
     với content đang có. Ngân sách ngắn (`OTA_BOOT_MS`) là thứ biến "không
     chặn" từ một lời hứa thành một con số. */
  if (CONTENT_URL) {
    try {
      const r = await checkForUpdate(content.contentVersion, {
        contentUrl: CONTENT_URL,
        timeoutMs: OTA_BOOT_MS,
        onProgress: bootSay,
      });
      if (r.status === "ready") {
        /* Đọc lại: `resolveContent` nay nhặt được đúng pack vừa cất, nên bản
           mới có hiệu lực NGAY phiên này chứ không phải phiên sau. */
        bootSay(`Đang áp dụng nội dung ${r.contentVersion}…`);
        await napNoiDung();
      } else if (r.status === "incompatible") {
        console.warn(`[ota] cần core ${r.requiresCore}, đang chạy ${CORE_VERSION}`);
      } else if (r.status === "invalid") {
        console.warn("[ota] pack bị từ chối:", r.problems);
      }
    } catch {
      /* Hỏng ở bất cứ đâu cũng chỉ có một hệ quả: chơi bằng content đang có. */
    }
  }
  bootSay("Đang dựng nông trại…");

  const root = document.documentElement;
  root.dataset["content"] = content.contentVersion;
  root.dataset["contentSource"] = contentSource;
  root.dataset["core"] = CORE_VERSION;

  /* `coCham` là KHẢ NĂNG của máy, không phải chế độ đang chơi.
     Trước đây cùng một cái cờ làm cả hai việc, và đó là lỗi: laptop có màn cảm
     ứng bị coi là điện thoại vĩnh viễn — giấu số phím hotbar, in bảng hướng dẫn
     kiểu chạm — dù người ta đang gõ bàn phím. Giờ khả năng nằm ở
     `body.touch`, còn thứ đang hiển thị nằm ở `body[data-input]`, và cái sau
     đổi theo thiết bị vừa dùng. */
  const coCham = matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;
  if (coCham) document.body.classList.add("touch");

  // Settings là sở thích của MÁY, không thuộc ván chơi — localStorage riêng,
  // không đi vào save. Nạp SỚM vì menu và camera tham chiếu tới nó lúc dựng.
  let settings: Settings = loadSettings();
  applySettings(settings);
  setHaptics(settings.haptics);
  setMuted(!settings.sound);

  /* ---- 2. mỹ thuật ---- */
  /* Đợt 24 nhân số pixel của atlas lên bốn lần, nên thời gian dựng nó thành
     một con số phải THEO DÕI ĐƯỢC chứ không phải đoán: nếu nó kéo dài màn hình
     chờ thấy được thì phải chuyển sang dựng lười theo lô. Chỉ đo ở bản DEV. */
  const atlasT0 = import.meta.env?.DEV ? performance.now() : 0;
  const atlas = buildAtlas(content);
  if (import.meta.env?.DEV)
    console.info(`[atlas] dựng xong trong ${(performance.now() - atlasT0).toFixed(1)} ms`);

  /* ---- 3. state: tiếp tục save cũ hoặc bắt đầu mới ----

     Bốn kết cục, và chỉ MỘT trong bốn được phép ghi đè lên khoá save:
       · đọc được, hợp lệ         → chơi tiếp
       · của bản MỚI HƠN          → màn chặn, thoát boot, không đụng gì cả
       · hỏng / quá cũ / vỡ bất biến → SAO LƯU trước, rồi mới bắt đầu ván mới
       · không có gì              → ván mới
     Trước đây ba kết cục cuối cùng đi chung một nhánh, im lặng, và không sao lưu. */
  let initial: GameState;
  /** Đang chơi tiếp một save cũ (không phải ván mới) — để khỏi bật hướng dẫn. */
  let tiepTucSave = false;
  const vanMoiSauKhiSaoLuu = async (ly: string) => {
    const coBan = await backupSave();
    contentWarnings.push(
      `${ly} — đã bắt đầu nông trại mới.` + (coBan ? " Bản cũ được giữ ở ô sao lưu (Save ra/vào)." : ""),
    );
    return createNewGame(content);
  };
  const doc = await loadSlot();
  if (doc.kind === "ok") {
    const mg = migrateSaveEx(doc.data);
    if (!mg.ok && mg.why === "newer") {
      chanSaveMoiHon();
      return;
    }
    if (!mg.ok) initial = await vanMoiSauKhiSaoLuu("Save cũ quá cũ, không đọc được");
    else {
      /* Migrate xong thì PHẢI kiểm, kể cả ở bản phát hành — và cùng một hàm
         với Nạp/Nhập (`napState`), để ba lối vào chung một luật. */
      const r = napState(mg.state, content, "boot");
      if (r.ok) {
        initial = r.state;
        contentWarnings.push(...r.notes);
        tiepTucSave = true;
      } else initial = await vanMoiSauKhiSaoLuu(r.why);
    }
  } else if (doc.kind === "corrupt") initial = await vanMoiSauKhiSaoLuu("Save cũ bị hỏng, không đọc được");
  else initial = createNewGame(content);

  const store: Store = createStore(initial, content, {
    validate: import.meta.env.DEV,
    strict: false,
    historyLimit: 0,
  });





  /* ---- 4. hệ thống hiển thị ---- */
  const canvas = $<HTMLCanvasElement>("#game");
  const stage = $("#stage");

  const camera = createCamera({ tile: TILE });
  camera.setZoom(settings.zoom);
  camera.setWorld(initial.w * TILE, initial.h * TILE);
  const renderer = createRenderer(canvas, atlas, camera);

  observeScreen(stage, (info) => {
    if (camera.setSize(info.cssW, info.cssH, info.dpr)) renderer.applyViewport();
    document.body.dataset["orientation"] = info.orientation;
  });
  camera.jumpTo(initial.player.x, initial.player.y);
  const hud = createHud($("#hud"), atlas);
  hud.onAnimalClose(() => {
    cardAnimal = null;
  });
  hud.onAnimalCycle((d) => cycleAnimal(store.getState(), d));
  /* MỔ THỊT — hỏi một câu vì không hoàn tác được, rồi dispatch cái action đã
     nằm sẵn trong reducer từ lâu mà chưa có nút nào gọi. */
  hud.onAnimalSlaughter(() => {
    const s = store.getState();
    const e = s.entities.find((x) => x.id === cardAnimal && x.kind === "animal" && x.map === s.mapId);
    if (!e) return;
    const st = animalStats(e, content);
    if (!st) return;
    const ten = content.animals[e.def]?.name ?? "con vật";
    const duoc = st.meat
      ? `${st.meat.min === st.meat.max ? st.meat.min : `${st.meat.min}–${st.meat.max}`} ${itemName(st.meat.id, content)}`
      : "thịt";
    menus.confirm(`Mổ ${ten} này?`, `Không hoàn tác được. Được ${duoc}.`, () => {
      const truoc = store.getState().entities.length;
      store.dispatch({ t: "SLAUGHTER", x: Math.floor(e.x / TILE), y: Math.floor(e.y / TILE) });
      if (store.getState().entities.length < truoc) {
        cardAnimal = null;
        play("coin");
        buzz("heavy");
      } else deny();
    });
  });
  const toasts = createToasts($("#toasts"));

  const tutorial = createTutorial($("#tutorial"), () => {
    if (!settings.tutorialSeen) setSetting("tutorialSeen", true);
  });

  /** Đổi MỘT khoá settings: lưu, áp dụng lên <body>, camera, rung. */
  function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): Settings {
    settings = { ...settings, [key]: value };
    saveSettings(settings);
    applySettings(settings);
    setHaptics(settings.haptics);
    setMuted(!settings.sound);
    /* Chế độ điều khiển sống trong `input.ts` (nó là chỗ duy nhất thấy tín hiệu
       thô), nên `applySettings` không với tới — đẩy tay. */
    if (key === "inputMode")
      input.setInputMode(settings.inputMode === "auto" ? null : settings.inputMode);
    if (key === "zoom" && camera.setZoom(settings.zoom)) {
      renderer.applyViewport();
      const p = store.getState().player;
      camera.jumpTo(p.x, p.y);
    }
    return settings;
  }

  const menus = createMenus($("#modal-root"), atlas, () => store.getState(), () => content, {
    buy: (id, n) => store.dispatch({ t: "BUY", id, n }),
    buyBoat: (id, n) => store.dispatch({ t: "BUY_BOAT", id, n }),
    drop: (slot) => store.dispatch({ t: "DROP", slot }),
    eat: (slot) => store.dispatch({ t: "EAT", slot }),
    swap: (a, b) => {
      store.dispatch({ t: "SWAP", a, b });
      buzz("tap");
    },
    craft: (id) => store.dispatch({ t: "CRAFT", id }),
    canCraft: (id) => canCraft(store.getState(), content, id),
    missingFor: (id) => missingFor(store.getState(), content, id),
    debug: (op, n) => store.dispatch({ t: "DEBUG", op, ...(n === undefined ? {} : { n }) }),
    sell: (id, n) => store.dispatch({ t: "SELL", id, n }),
    sellAll: () => store.dispatch({ t: "SELL_ALL" }),
    save: async () => {
      const r = await saveGame(store.snapshot());
      toasts.say(r.ok ? "Đã lưu game." : "Không lưu được — bộ nhớ trình duyệt bị chặn?", r.ok ? "good" : "bad");
    },
    load: async () => {
      const d = await loadSlot();
      if (d.kind === "empty") return toasts.say("Chưa có bản lưu nào.", "bad");
      if (d.kind === "corrupt") return toasts.say("Bản lưu bị hỏng, không đọc được.", "bad");
      if (nhanSave(d.data, "load")) toasts.say("Đã tải bản lưu.", "good");
    },
    exportSave: () => {
      exportToFile(store.snapshot());
      toasts.say("Đang tải file save về máy.", "good");
    },
    importSave: async () => {
      const d = await importFromFile();
      if (!d) return toasts.say("File save không hợp lệ.", "bad");
      if (nhanSave(d, "import")) toasts.say("Đã nhập bản lưu.", "good");
    },
    say: (text, kind) => toasts.say(text, kind),
    hasBackup: async () => (await loadSlot(SLOT_BACKUP)).kind !== "empty",
    restoreBackup: async () => {
      const d = await loadSlot(SLOT_BACKUP);
      if (d.kind === "empty") return toasts.say("Không có bản sao lưu nào.", "bad");
      if (d.kind === "corrupt")
        return toasts.say("Bản sao lưu bị hỏng — hãy xuất ra file để giữ lại.", "bad");
      if (nhanSave(d.data, "load")) toasts.say("Đã khôi phục bản sao lưu.", "good");
    },
    newGame: () => adoptState(createNewGame(content)),
    /* Đi qua `setSetting` để CÓ LƯU — trước đây chỉ lật một biến trong sfx.ts,
       tải lại trang là kêu lại. */
    toggleMute: () => {
      setSetting("sound", !settings.sound);
      return isMuted();
    },
    isMuted,
    settings: () => settings,
    setSetting,
    revertContent: async () => {
      await revertToBundled();
      toasts.say(content.strings.msg["otaReverted"] ?? "Đã quay về nội dung đóng kèm.", "good");
    },
    contentInfo: () => ({
      version: content.contentVersion,
      source: contentSource,
      pending: pendingVersion,
    }),
    storePut: (slot, n) => store.dispatch({ t: "STORE_PUT", slot, n }),
    storeTake: (slot, n) => store.dispatch({ t: "STORE_TAKE", slot, n }),
    storePutAll: () => store.dispatch({ t: "STORE_PUT_ALL" }),
    storeSellAll: () => store.dispatch({ t: "STORE_SELL_ALL" }),
    buyAnimal: (def) => store.dispatch({ t: "BUY_ANIMAL", def }),
    penGather: (pen) => store.dispatch({ t: "PEN_GATHER", pen }),
    penPour: (pen) => store.dispatch({ t: "PEN_POUR", pen }),
    /* Bảng khu → thẻ MỘT CON: đóng menu rồi mở thẻ. Thẻ vẽ đè lên mặt sân chứ
       không nằm trong menu, nên hai thứ không chồng lên nhau được. */
    showAnimal: (id) => {
      menus.close();
      cardAnimal = id;
    },
    hire: (job) => store.dispatch({ t: "HIRE", job }),
    fire: (id) => store.dispatch({ t: "FIRE", id }),
    openBuild: (id) => buildUI.open(id),
    toggleDevPanel: () => devPanel.toggle(),
    canInstall: () => installPrompt !== null,
    padInfo: () => input.padInfo(),
    buildMode: () => buildUI.open(),
    /* Công tắc TỰ ĐỘNG LÀM. Trước đợt này nó là một nút tròn 52px nằm chung
       cụm với nút ngữ cảnh — ba ô chạm chen nhau đúng chỗ ngón cái quét qua
       nhiều nhất, mà lại là thứ bật một lần rồi để đó. */
    autoWork: () => autoWork,
    setAutoWork: (on: boolean) => setAuto(on, null),

    /* Hỏi HAI thứ trong một lần bấm, vì người chơi chỉ biết một khái niệm
       "bản mới": nội dung OTA (giá, cây, mùa) và mã game (service worker).
       Trả về đúng một câu để hiện thẳng dưới nút. */
    async checkUpdate() {
      let cauNoiDung = "Nội dung đã là bản mới nhất.";
      if (CONTENT_URL) {
        const r = await checkForUpdate(content.contentVersion, { contentUrl: CONTENT_URL });
        if (r.status === "ready") {
          pendingVersion = r.contentVersion;
          cauNoiDung = `Có nội dung ${r.contentVersion} — bấm "Cập nhật ngay".`;
        } else if (r.status === "incompatible")
          cauNoiDung = `Bản nội dung mới cần core ${r.requiresCore}, máy đang chạy ${CORE_VERSION}.`;
        else if (r.status === "invalid") cauNoiDung = "Bản nội dung trên máy chủ bị lỗi — bỏ qua.";
        else if (r.status === "error") cauNoiDung = "Không hỏi được máy chủ (mất mạng?).";
      }
      // Giục service worker đi hỏi ngay thay vì chờ nhịp kiểm tra của trình duyệt.
      try {
        const reg = await navigator.serviceWorker?.getRegistration();
        if (reg) await reg.update();
      } catch {
        /* không có service worker (bản dev, hoặc trình duyệt chặn) — bỏ qua */
      }
      return cauNoiDung;
    },

    forceUpdate: buocCapNhat,
    install: async () => {
      const p = installPrompt;
      if (!p) return;
      installPrompt = null;
      menus.close();
      await p.prompt();
      const r = await p.userChoice;
      if (r.outcome === "accepted") toasts.say("Đã cài OniFarm về máy.", "good");
    },
    replayTutorial: () => tutorial.start(buocHuongDan()),
    isTouch: () => input.mode() === "touch",
    inputMode: () => input.mode(),
  });

  /**
   * Nhận một save từ Nạp/Nhập: migrate → KIỂM → thay state. Trả về `true` nếu
   * đã thay. Từ chối thì nói lý do và GIỮ NGUYÊN ván đang chơi — nhập nhầm một
   * file cụt không được phép làm mất ván đang dở.
   *
   * Cùng một `napState` với đường Boot. Trước đây hai đường này bỏ qua kiểm
   * bất biến, và `store.replace` ở bản phát hành thì không kiểm gì.
   */
  function nhanSave(d: SaveData, nguon: "load" | "import"): boolean {
    const mg = migrateSaveEx(d);
    if (!mg.ok) {
      toasts.say(
        mg.why === "newer"
          ? "Bản lưu này của phiên bản MỚI hơn — hãy cập nhật game rồi thử lại."
          : "Bản lưu quá cũ, không đọc được.",
        "bad",
      );
      return false;
    }
    const r = napState(mg.state, content, nguon);
    if (!r.ok) {
      toasts.say(`${r.why} — giữ nguyên ván đang chơi.`, "bad");
      return false;
    }
    adoptState(r.state);
    for (const n of r.notes) toasts.say(n, "info");
    menus.close();
    return true;
  }

  /** Thay state (tải save, chơi mới): camera phải NHẢY tới chỗ mới. */
  function adoptState(next: GameState) {
    store.replace(next);
    camera.setWorld(next.w * TILE, next.h * TILE);
    camera.jumpTo(next.player.x, next.player.y);
    lastMap = next.mapId;
    aimed = null;
  }

  hud.onSelect((slot) => {
    store.dispatch({ t: "SELECT", slot });
    buzz("tap");
  });
  hud.onBag(() => {
    if (tutorial.isOpen()) return;
    if (menus.isOpen()) menus.close();
    else menus.openBag();
  });

  /* Bảng gỡ lỗi NỔI. CỐ Ý không tính vào biến `modal`: game vẫn chạy, thời gian
     vẫn trôi, nhân vật vẫn đi được trong lúc bảng mở — đó mới là chỗ nó hữu ích,
     bấm một lệnh rồi nhìn thẳng vào thế giới thấy ngay kết quả. */
  const buildUI = createBuildMode($("#buildmode"), atlas, {
    // Chọn công trình sống trong chính bảng đó, không đụng tới hotbar: từ khi
    // tiền trả theo số ô vẽ thì không phải "cầm" nó lên nữa.
    select: () => {},
  });

  const devPanel = createDevPanel($("#devpanel"), {
    debug: (op, n) => store.dispatch({ t: "DEBUG", op, ...(n === undefined ? {} : { n }) }),
  });

  const minimap = createMinimap($("#minimap"));
  minimap.onPick((tx, ty) => {
    if (menus.isOpen()) return;
    nav.goTo(store.getState(), content, tx, ty, { act: false });
  });

  /* ---- 5. input ---- */
  const stickZone = document.querySelector<HTMLElement>("#stick");
  const input = createInput(canvas, {
    toWorld: (cx, cy) => {
      const r = stage.getBoundingClientRect();
      return camera.screenToWorld(cx - r.left, cy - r.top);
    },
    /* Bảng gỡ lỗi tính là "đang mở giao diện" ĐỐI VỚI TAY CẦM: nav đi vào đó
       thay vì đi vào thế giới. Nhưng nó KHÔNG chặn thời gian — main.ts tính
       `modal` riêng, và bảng gỡ lỗi cố ý không nằm trong đó (xem devpanel.ts).
       Không có dòng này thì bảng gỡ lỗi là thứ duy nhất trong game không bấm
       được bằng tay cầm. */
    isModalOpen: () => menus.isOpen() || tutorial.isOpen() || devPanel.isOpen(),
    coCham,
    initialMode: coCham ? "touch" : "kbm",
    inputMode: settings.inputMode === "auto" ? null : settings.inputMode,
    ...(stickZone
      ? {
          joystick: {
            zone: stickZone,
            base: stickZone.querySelector<HTMLElement>(".base")!,
            knob: stickZone.querySelector<HTMLElement>(".knob")!,
          },
        }
      : {}),
  });

  /** Bảng hướng dẫn theo ĐÚNG thứ người chơi đang cầm. */
  const buocHuongDan = () => {
    const m = input.mode();
    return m === "pad" ? PAD_STEPS : m === "touch" ? TOUCH_STEPS : DESKTOP_STEPS;
  };

  /* MỘT giá trị `data-input` cho cả giao diện: `touch` | `pad` | `pad-std` |
     `kbm`. Trước đây `body.touch` (chốt lúc boot) và `data-input` (chỉ nói "có
     tay cầm cắm") là hai cờ độc lập, nên điện thoại cắm tay cầm hiện cùng lúc
     cụm nút chạm lẫn hai dải gợi ý tay cầm. Giờ đúng một cờ, và nó theo THIẾT
     BỊ VỪA DÙNG.

     Vẫn tách `pad` với `pad-std`: tay cầm không theo sơ đồ chuẩn chỉ có cần gạt
     và hai nút mặt, nên phải để cụm nút chạm bấm được, nếu không là bịt nốt
     đường vào cuối cùng của chế độ xây dựng. */
  const veCheDo = () => {
    const m = input.mode();
    document.body.dataset["input"] = m === "pad" ? (input.padInfo().standard ? "pad-std" : "pad") : m;
  };
  veCheDo();
  input.onModeChange(veCheDo);

  /* Nối đường rung TAY CẦM vào cùng cái công tắc với rung điện thoại.
     `gamepad.ts` có sẵn `rumble()` đầy đủ — dual-rumble, có `.catch()`, có
     kiểm tra kiểu động cho Firefox/Safari — nhưng trước đây nó chỉ được gọi
     ĐÚNG MỘT LẦN trong cả game, lúc vừa nhận tay cầm. Cày, tưới, thu hoạch,
     chặt cây, thao tác hụt: không cú nào tới tay. */
  setPadRumble((ms, strong) => input.rumble(ms, strong));

  /* Nút XÂY nổi ở góc đã GỠ. Nó là ô chạm nhỏ nhất và vướng nhất trên màn
     hình, mà lại là đường vào THỨ BA cho một việc đã có hai đường tự nhiên
     hơn: ô đầu tiên trong lưới menu Tạm dừng, và cầm một công trình rồi bấm
     nút chính (vào thẳng chế độ xây, đúng cái người chơi đang định làm). */

  for (const [sel, code] of [
    ["#abtn .a", "Space"],
    ["#abtn .b", "KeyE"],
    ["#abtn .t", "KeyQ"],
    ["#abtn .y", "KeyI"],
    ["#sysbtn .menu", "Escape"],
  ] as [string, string][]) {
    const el = document.querySelector<HTMLElement>(sel);
    if (el) bindTouchButton(el, code);
  }

  /* ---- 6. phản hồi: âm thanh + hạt + rung, suy ra từ thay đổi thống kê ---- */
  let lastStats: Stats = store.getState().stats;
  let lastMoney = store.getState().money;
  let lastDay = store.getState().day;
  /** Ô vừa được USE — để bắn hạt đúng chỗ. */
  let lastUse: { x: number; y: number } | null = null;

  const countBuilt = (st: Stats) => Object.values(st.built).reduce((x, y) => x + y, 0);

  const feedback = (
    sfx: Parameters<typeof play>[0],
    fx: BurstKind | null,
    hap: "success" | "heavy" = "success",
    at: { x: number; y: number } | null = lastUse,
  ) => {
    play(sfx);
    buzz(hap);
    if (fx && at) renderer.burst(fx, at.x, at.y);
  };
  const chanNhanVat = (s: GameState) => ({ x: Math.floor(s.player.x / TILE), y: Math.floor(s.player.y / TILE) });

  let lastMapForMoney = store.getState().mapId;
  store.subscribe((s) => {
    const a = s.stats;
    const b = lastStats;
    if (a.tilled > b.tilled) feedback("till", "dust");
    else if (a.watered > b.watered) feedback("water", "water");
    else if (a.planted > b.planted) feedback("plant", "leaf");
    else if (a.harvested > b.harvested) feedback("harvest", "leaf");
    /* VẮT SỮA / NHẶT TRỨNG và CHỮA CÂY từng không có mặt ở đây: không tiếng,
       không hạt, không rung — chỉ một dòng toast. Vắt sữa là động từ trung tâm
       của cả mảng chăn nuôi mà bấm xong thấy như không có gì xảy ra. */
    else if ((a.gathered ?? 0) > (b.gathered ?? 0)) feedback("harvest", "leaf", "success", lastUse ?? chanNhanVat(s));
    else if ((a.cured ?? 0) > (b.cured ?? 0)) feedback("plant", "leaf", "success", lastUse ?? chanNhanVat(s));
    else if (countBuilt(a) > countBuilt(b)) feedback("build", "spark", "heavy");
    else if (s.money > lastMoney) {
      play("coin");
      /* Xu bắn ở CHÂN NHÂN VẬT, không phải ở `lastUse` — bán từ menu thì
         `lastUse` là ô cuốc lần cuối, hay ở ngoài khung nhìn, thỉnh thoảng ở
         bản đồ khác. */
      renderer.burst("coin", Math.floor(s.player.x / TILE), Math.floor(s.player.y / TILE));
    } else if (s.money < lastMoney && s.mapId === lastMapForMoney) {
      /* MUA thì tiền đi xuống — nhánh trên không bao giờ nổ. Không có tiếng
         "mua" thì bấm mua một con bò 800đ cũng câm như bấm hụt. Rào theo bản
         đồ để đổi bản đồ (không đổi tiền) không kêu nhầm. */
      play("buy");
      buzz("tap");
    }
    lastMapForMoney = s.mapId;
    if (s.day > lastDay) play("sleep");
    lastStats = a;
    lastMoney = s.money;
    lastDay = s.day;
  });

  // chặt/đập không có ô thống kê riêng: bắt qua tham chiếu lưới đổi + hp giảm
  let lastTiles = store.getState().tiles;
  store.subscribe((s) => {
    if (s.tiles === lastTiles || !lastUse) {
      lastTiles = s.tiles;
      return;
    }
    const i = lastUse.y * s.w + lastUse.x;
    const before = lastTiles[i];
    const after = s.tiles[i];
    lastTiles = s.tiles;
    if (!before || !after || s.mapId !== lastMap) return;
    if (before.prop && (after.hp < before.hp || after.prop !== before.prop)) {
      const def = content.props[before.prop];
      const mine = def?.tool === "MINE";
      play(mine ? "till" : "harvest");
      buzz("heavy");
      renderer.burst(mine ? "stone" : "leaf", lastUse.x, lastUse.y);
    }
  });

  // âm thanh chỉ được phép khởi tạo sau cử chỉ đầu tiên (luật autoplay)
  const unlockAudio = () => {
    initAudio();
    window.removeEventListener("pointerdown", unlockAudio);
    window.removeEventListener("keydown", unlockAudio);
  };
  window.addEventListener("pointerdown", unlockAudio);
  window.addEventListener("keydown", unlockAudio);

  /* ---- 7. tự lưu ---- */
  let dirtySince = 0;
  /** Đã báo "không lưu được" chưa — báo MỘT lần, không phải mỗi 30 giây. */
  let daBaoLoiLuu = false;
  const autosave = async () => {
    dirtySince = 0;
    const r = await saveGame(store.snapshot());
    if (r.ok) {
      daBaoLoiLuu = false;
      return;
    }
    /* Trước đây kết quả bị vứt đi (`void autosave()` ở mọi chỗ gọi): hết dung
       lượng hay bị chặn là hỏng im lặng vĩnh viễn, người chơi chỉ biết khi mở
       lại và thấy nông trại lùi một tuần. */
    if (daBaoLoiLuu) return;
    daBaoLoiLuu = true;
    toasts.say("KHÔNG LƯU ĐƯỢC — bộ nhớ trình duyệt đầy hoặc bị chặn. Hãy xuất save ra file.", "bad");
  };
  let savedDay = store.getState().day;
  /** Mốc bắt đầu hiệu ứng chuyển ngày (giây của vòng lặp), 0 = không có. */
  let dayFadeAt = 0;
  store.subscribe((s) => {
    if (s.day > savedDay) {
      const passedOut = s.energy < content.balance.energyMax * 0.75;
      savedDay = s.day;
      void autosave();
      dayFadeAt = elapsed;
      const wxNow = weatherDef(s, content).name;
      const wxNext = forecastDef(s, content).name;
      hud.dayBanner(
        s.day,
        (passedOut ? "Ngất giữa đồng — dậy muộn và mệt · " : "") + `${wxNow} · mai: ${wxNext}`,
      );
    } else dirtySince++;
  });
  setInterval(() => {
    if (dirtySince > 0) void autosave();
  }, 30_000);
  addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden" && dirtySince > 0) void autosave();
  });
  addEventListener("pagehide", () => {
    if (dirtySince > 0) void autosave();
  });

  /* ---- 8. ô đang nhắm ---- */
  function targetTile(s: GameState, forceFacing = false): Cursor | null {
    let p = forceFacing || input.stickActive() ? null : input.pointer();
    /* Chuột chưa nhúc nhích kể từ lúc bấm nút MỤC TIÊU thì coi như không có
       chuột: mục tiêu vừa chọn phải sống được ít nhất tới khi người chơi thật
       sự rê tay. */
    if (p && chuotLucChon && p.x === chuotLucChon.x && p.y === chuotLucChon.y) p = null;
    else if (p) chuotLucChon = null;

    // Cổng theo TẦM NGẮM (`AIM_RADIUS`), không theo tầm với — cùng bán kính mà
    // `reachTargets` liệt kê và `aimStillValid` giữ. Xem chú thích ở AIM_RADIUS.
    if (!p && !forceFacing && aimed && aimStillValid(s, aimed))
      return { x: aimed.x, y: aimed.y, ok: tileActionable(s, aimed.x, aimed.y) };

    let tx: number;
    let ty: number;
    if (p) {
      tx = Math.max(0, Math.min(s.w - 1, Math.floor(p.x / TILE)));
      ty = Math.max(0, Math.min(s.h - 1, Math.floor(p.y / TILE)));
    } else {
      const f = facingTile(s, TILE);
      tx = f.x;
      ty = f.y;
    }
    if (tx < 0 || ty < 0 || tx >= s.w || ty >= s.h) return null;
    return { x: tx, y: ty, ok: tileActionable(s, tx, ty) };
  }

  /** MỘT nguồn sự thật: hỏi thẳng luật của game, không tự tính lại. */
  function inReachOf(s: GameState, tx: number, ty: number): boolean {
    return inReach(s, tx, ty);
  }

  /* ---- 9. bấm-để-đi ---- */
  const nav = createNavigator();

  let lastPos = { x: store.getState().player.x, y: store.getState().player.y };
  let lastMap = store.getState().mapId;
  store.subscribe((st) => {
    const mapChanged = st.mapId !== lastMap;
    if (mapChanged) {
      lastMap = st.mapId;
      camera.setWorld(st.w * TILE, st.h * TILE);
      aimed = null;
    }
    if (mapChanged || Math.hypot(st.player.x - lastPos.x, st.player.y - lastPos.y) > TILE * 4) {
      camera.jumpTo(st.player.x, st.player.y);
      nav.cancel();
    }
    lastPos = { x: st.player.x, y: st.player.y };
  });

  /**
   * MỤC TIÊU — ô mà nút CHÍNH sẽ tác động vào.
   *
   * Từ Đợt 27 đây KHÔNG còn là "ô vừa chạm". Chạm màn hình giờ chỉ có nghĩa
   * ĐI, nên mục tiêu phải được chọn riêng: nút MỤC TIÊU (phím Q), cần phải tay
   * cầm, hoặc rê chuột trên máy tính. Hệ thống chỉ đặt nó ở đúng một chỗ — lúc
   * tới đích một chuyến (`nav.takeArrival`) — để chuỗi cày → gieo → tưới trên
   * cùng một ô không phải chọn lại sau mỗi lần đi.
   *
   * Nó tự rơi khi ra khỏi TẦM VỚI, và chỉ khi ấy: xem `aimStillValid`, luật
   * dùng chung với kịch bản sim 168.
   *
   * `thuCong` — mục tiêu này do CHÍNH TAY người chơi chọn (nút MỤC TIÊU / phím
   * Q / cần phải tay cầm), chứ không phải do hệ thống tự đặt lúc tới đích hay
   * lúc chạy chuyến tự động. Cần phân biệt vì `pressCursor` phải cho lựa chọn
   * của người chơi THẮNG ô đích chuyến đi — xem chú thích ở đó.
   *
   * Cờ nằm TRONG cùng một biến chứ không tách ra thành `let aimThuCong` riêng:
   * hai biến phải tắt/bật cùng nhau ở bảy chỗ khác nhau, và quên một chỗ là mục
   * tiêu dính vĩnh viễn. Gộp lại thì không thể lệch.
   */
  let aimed: { x: number; y: number; thuCong: boolean } | null = null;

  /**
   * Vị trí chuột lúc người chơi chọn mục tiêu bằng NÚT/PHÍM.
   *
   * Vì sao cần: `targetTile` cho chuột thắng `aimed` (Cường chốt "rê chuột thì
   * ngắm luôn"), mà con trỏ chuột thì luôn nằm đâu đó trên màn hình. Không có
   * mốc này thì bấm Q xong nhích chuột một pixel là mục tiêu vừa chọn biến
   * mất — người chơi thấy cái nút mới chớp tắt vô nghĩa. Bỏ qua chuột cho tới
   * khi nó THẬT SỰ rời khỏi chỗ cũ.
   */
  let chuotLucChon: { x: number; y: number } | null = null;

  /**
   * Con vật đang MỞ bảng thống kê, theo id. `null` = không mở bảng nào.
   *
   * Cố ý theo Ý ĐỊNH của người chơi chứ không theo khoảng cách. Bản đầu tự hiện
   * bảng cho con vật gần nhất, và trên điện thoại nó bật lên mỗi lần đi ngang
   * qua chuồng, che một phần tư màn hình đúng lúc đang cần nhìn ruộng. Giờ:
   * chạm vào con vật thì mở, bấm × hoặc chạm chỗ khác thì đóng.
   */
  let cardAnimal: number | null = null;

  /**
   * Hút cú chạm về ô CÓ NGHĨA gần nhất — **chỉ dùng trong chế độ XÂY**.
   *
   * Từ Đợt 27 nhánh đi bộ dùng ô THÔ. Phép hút này sinh ra hồi cú chạm còn
   * phải chọn ô để LÀM VIỆC, và khi ấy nó đúng: ngón tay to hơn một ô 32px.
   * Với nghĩa mới ("chạm là ĐI") nó thành sai hẳn — chạm vào khoảnh cỏ trống
   * cạnh gốc cây thì nhân vật lại đi sang ô gốc cây, tức là đi chỗ khác chỗ
   * người chơi vừa chỉ. Trong chế độ xây thì người chơi đang chỉ vào một Ô để
   * đặt công trình, nên ở đó nó vẫn đúng như cũ.
   */
  function snapTap(s: GameState, wx: number, wy: number): { x: number; y: number } {
    const raw = {
      x: Math.max(0, Math.min(s.w - 1, Math.floor(wx / TILE))),
      y: Math.max(0, Math.min(s.h - 1, Math.floor(wy / TILE))),
    };
    const radius = Math.max(4, Math.min(16, 26 / camera.viewport.scale));
    let best: { x: number; y: number } | null = null;
    let bestScore = Infinity;

    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const tx = raw.x + dx;
        const ty = raw.y + dy;
        if (tx < 0 || ty < 0 || tx >= s.w || ty >= s.h) continue;
        const d = Math.hypot(tx * TILE + TILE / 2 - wx, ty * TILE + TILE / 2 - wy);
        if (d > radius && !(dx === 0 && dy === 0)) continue;
        const useful =
          canUseAt(s, content, tx, ty, true) !== null || interactAt(s, content, tx, ty) !== null;
        const score = (useful ? 0 : 1000) + d;
        if (score < bestScore) {
          bestScore = score;
          best = { x: tx, y: ty };
        }
      }
    }
    return best ?? raw;
  }

  function tileActionable(s: GameState, tx: number, ty: number): boolean {
    const t = s.tiles[ty * s.w + tx];
    if (!t) return false;
    if (t.prop) {
      const def = content.props[t.prop];
      return !!def && (!!def.hits || !!def.interact);
    }
    return t.g !== "water" || !!content.tiles.grounds["water"]?.interact;
  }

  /* ---- XÂY THEO TUYẾN: KÉO --------------------------------------------
     Hàng rào và đường nhựa là thứ dựng thành ĐOẠN, không phải đặt từng ô: mười
     ô rào là mười cú bấm, lệch một ô là phải đập đi làm lại. Nên chúng không
     dùng đường "chọn rồi ĐẶT" như vòi tưới hay pin mặt trời — cầm chúng lên là
     vào ngay chế độ kéo: ấn ở đầu đoạn, rê tới cuối, nhả tay.

     Loại nào kéo được là do CONTENT nói (`BuildingDef.drag`), không phải
     `switch (id)` trong này — thêm "mương nước" sau chỉ là thêm một dòng JSON.

     Trạng thái CỐ Ý không nằm trong `GameState`: nó là ý định đang vẽ dở, cùng
     loại với đích của `nav`. Bỏ dở giữa chừng thì không có gì phải dọn trong
     save. */
  let lineFrom: { x: number; y: number } | null = null;
  /** Ô cuối đang rê tới — trên điện thoại con trỏ chuột không tồn tại, nên
   *  đường xem trước phải bám vào ĐÂY chứ không bám `cursor`. */
  let lineTo: { x: number; y: number } | null = null;
  /**
   * Con trỏ Ô của TAY CẦM trong chế độ xây dựng.
   *
   * Tay cầm không có chuột, mà cả chế độ xây dựng dựng trên việc "trỏ vào ô rồi
   * rê". Nên phải có một con trỏ ảo: cần gạt rê nó đi, A đặt đầu đoạn, A lần
   * nữa là xây, B thoát. Không có nó thì chế độ xây dựng — thứ DUY NHẤT đặt
   * được công trình — hoàn toàn không với tới được bằng tay cầm.
   */
  let padCursor: { x: number; y: number } | null = null;



  /** Đang cầm công trình gì (id trần), null nếu không cầm công trình nào. */
  function heldBuilding(s: GameState): string | null {
    const held = s.inv[s.sel];
    if (!held?.id.startsWith("build:")) return null;
    const id = held.id.slice(6);
    return content.buildings[id] ? id : null;
  }

  /** Bật/tắt luồng ý định KÉO. Gọi mỗi khung hình — rẻ, và không phải nhớ tắt
   *  ở mười chỗ khác nhau. */
  const setLineMode = (on: boolean) => {
    input.setDrag(on);
    if (!on) {
      lineFrom = null;
      lineTo = null;
    }
  };

  function holdingSolidBuilding(s: GameState): boolean {
    const held = s.inv[s.sel];
    if (!held?.id.startsWith("build:")) return false;
    const def = content.buildings[held.id.slice(6)];
    return !!def && def.kind === "object" && def.solid;
  }

  /** Thực thi một cú `interact` mà `pressPlan` đã chốt — không quét lại. */
  function doInteract(kind: InteractKind, x: number, y: number): boolean {
    buzz("tap");
    switch (kind) {
      case "SHOP":
        menus.openShop();
        return true;
      case "SELL":
        menus.openSell();
        return true;
      case "STORE":
        menus.openStore();
        return true;
      case "CRAFT":
        menus.openCraft();
        return true;
      case "SLEEP":
        /* Đi qua INTERACT chứ không phải SLEEP thẳng: reducer ĐẶT nhân vật lên
           giường rồi mới sang ngày (`balance.sleepSeconds`), và kiểm tầm với
           — trước đây nút chính bỏ qua cả hai. */
        store.dispatch({ t: "INTERACT", x, y });
        return true;
      case "REFILL":
        lastUse = { x, y };
        store.dispatch({ t: "REFILL" });
        renderer.burst("water", x, y);
        return true;
      case "PORTAL":
        store.dispatch({ t: "PORTAL", x, y });
        return true;
      default:
        return false;
    }
  }


  /* ---- TỰ ĐỘNG LÀM ----------------------------------------------------
     Cố ý KHÔNG nằm trong `GameState`: đây là ý định nhất thời của người chơi,
     đúng cùng lý do đích của `nav` không được lưu vào save (xem đầu
     core/navigate.ts). Nhờ vậy mốc này không phải tăng SAVE_VERSION.

     Đây là công tắc trong menu Tạm dừng — "trông nông trại hộ tôi", CÓ đổi
     tay. Khác hẳn CHUYẾN của nút chính (xem `run` bên dưới), thứ không bao
     giờ đổi ô hotbar. */
  let autoWork = false;
  /** Ô mà chế độ tự động đang làm dở — xem `autoStep`. */
  let autoAnchor: { x: number; y: number } | null = null;
  /** Số lần liên tiếp không tìm ra việc. Hai lần thì tự tắt. */
  let autoMiss = 0;
  /** Ô đang đi tới ĐỂ LÀM VIỆC (không phải do người chơi chạm). Tới nơi thì chỉ
   *  dùng công cụ, không mở hộp thoại nào. */
  let workGoal: { x: number; y: number; refill?: boolean; then?: "use" | "gather" | "interact" | "boat" } | null = null;
  /** Bao lâu rồi không có việc nào THÀNH CÔNG, tính bằng giây. */
  let autoIdle = 0;
  let autoMark = "";
  /** Bấy nhiêu giây không nhúc nhích thì tắt. Đủ dài để bao một nhát cuốc
   *  (actionSeconds) cộng quãng đi vài ô, đủ ngắn để không nhả một tràng toast. */
  const AUTO_IDLE_LIMIT = 4;

  /**
   * Vì sao đo bằng "không tiến triển" chứ không bắt từng lý do hỏng:
   * thao tác ở đây có HIỆU LỰC TRỄ — `USE` đặt `busy` ngay rồi mới kiểm năng
   * lượng lúc chạm đất — nên ngay sau khi dispatch thì không có cách nào biết
   * nhát này sẽ ăn hay trượt. Còn đếm bộ đếm thống kê thì đúng với MỌI lý do
   * hỏng cùng một lúc: hết năng lượng, túi đầy, hết hạt, kẹt đường.
   */
  const progressMark = (s: GameState): string =>
    // `water` nằm trong dấu vân tay vì MÚC NƯỚC cũng là tiến triển: không có nó
    // thì một chuyến đi múc dài là bốn giây "không tiến triển" và tự tắt.
    `${s.stats.tilled}|${s.stats.planted}|${s.stats.watered}|${s.stats.harvested}|${s.stats.cured}|${s.water}`;

  const stopAuto = (s: GameState) => {
    setAuto(false);
    /* NÓI RÕ VÌ SAO. "Quanh đây hết việc" đúng chữ nhưng vô ích khi ruộng còn
       nguyên mấy chục luống trống và thứ thiếu chỉ là nắm hạt trong tay. */
    const thieu = autoStopReason(s, content);
    toasts.say(
      s.energy < content.balance.energyCost.till
        ? "Hết năng lượng — đã tắt tự động làm."
        : thieu
          ? `${content.strings?.msg?.[thieu] ?? thieu} Đã tắt tự động làm.`
          : "Quanh đây hết việc — đã tắt tự động làm.",
      "info",
    );
  };
  /**
   * Chuyến do nút A nhận CHỈ làm đúng loại việc đó, rồi dừng.
   *
   * `null` = chế độ TỰ ĐỘNG đầy đủ (nút AUTO khi chơi chạm): đổi tay, đi hết
   * bậc ưu tiên THU → CHỮA → GIEO → TƯỚI → CÀY.
   *
   * Khác nhau ở chủ ngữ. Bấm nút AUTO là "trông nông trại hộ tôi". Bấm A trong
   * lúc cầm bao cám gà là "cho ăn cho xong đi" — và khi các máng đã đầy thì
   * việc ấy XONG. Đi cày tiếp là làm một thứ người chơi không hề bảo.
   */
  let autoOnly: UseKind = null;
  const setAuto = (on: boolean, only: UseKind = null) => {
    autoWork = on;
    autoOnly = on ? only : null;
    if (!on) autoAnchor = null;
    autoMiss = 0;
    // Đặt lại cả đồng hồ "không tiến triển", nếu không thì bật lại ngay sau khi
    // nó vừa tự tắt là tắt lần nữa tức thì (đồng hồ vẫn đang quá ngưỡng).
    autoIdle = 0;
    autoMark = "";
    /* Công tắc đã dời vào menu Tạm dừng, nên trên màn hình không còn cái nút
       nào sáng lên — mà "tự động đang bật" là trạng thái người chơi PHẢI thấy,
       nếu không thì nhân vật tự đi làm mà không rõ vì sao. Đẩy lên <body> để
       thanh gợi ý dưới hotbar đeo nó. */
    if (on) document.body.dataset["auto"] = "1";
    else delete document.body.dataset["auto"];
  };

  function tryUse(s: GameState, tx: number, ty: number): boolean {
    if (!inReachOf(s, tx, ty)) return false;
    lastUse = { x: tx, y: ty };
    store.dispatch({ t: "USE", x: tx, y: ty });
    return true;
  }

  /**
   * Bán kính của chế độ TỰ ĐỘNG: cả bản đồ.
   *
   * Từng để 12 ô, và đo được đúng cái hỏng: dọn sạch khu quanh chân xong là nó
   * tự tắt trong khi 14 cây chín còn đứng ở đầu kia ruộng. "Tự động làm" mà bỏ
   * lại việc thì người chơi vẫn phải đi kiểm tra — tức là không tự động.
   *
   * Quét cả lưới đắt hơn, nhưng chỉ chạy lúc vừa xong một việc và đang rảnh, và
   * thứ tự ưu tiên vẫn chọn ô GẦN NHẤT trong mỗi bậc — nên nó vẫn dọn quanh
   * chân trước, chỉ là hết việc gần thì đi tiếp thay vì bỏ cuộc.
   */
  const autoRadius = (s: GameState) => Math.max(s.w, s.h);

  /**
   * Ô MÚC NƯỚC gần nhất (ao, giếng) trên CẢ bản đồ.
   *
   * Cố ý không giới hạn bán kính như các việc khác. Việc trên ruộng thì có ở
   * khắp nơi nên gần thôi là đúng — đi xa hơn chỉ là lang thang.
   * Nhưng nguồn nước thì CÓ MỘT CHỖ: đo được lúc thử, người chơi đứng ở (33,25)
   * còn cái ao ở góc (5,4), tức 28 ô — ngoài bán kính, nên nó không đi múc và
   * cày tiếp cho tới lúc kiệt sức trong khi 83 ô đang khô.
   *
   * Quét cả lưới 48×37 chỉ tốn 1.776 phép so, và chỉ chạy đúng lúc bình cạn.
   */
  function nearestRefill(s: GameState): { x: number; y: number } | null {
    let best: { x: number; y: number } | null = null;
    let bestD = Infinity;
    for (let y = 0; y < s.h; y++)
      for (let x = 0; x < s.w; x++) {
        if (interactAt(s, content, x, y) !== "REFILL") continue;
        const d = Math.hypot(x * TILE + TILE / 2 - s.player.x, y * TILE + TILE / 2 - s.player.y);
        if (d < bestD) {
          bestD = d;
          best = { x, y };
        }
      }
    return best;
  }

  /**
   * MỘT bước của chế độ tự động.
   *
   * Khác CHUYẾN của nút chính (`runStep`) ở đúng một điểm, nhưng là điểm
   * quyết định: nó tự ĐỔI TAY. Chuyến chỉ biết thứ đang cầm, nên cầm cuốc là
   * cày hết rồi dừng — không gieo, không tưới, không thu. Ở đây `autoJob` chọn
   * việc trước rồi mới nói phải cầm ô hotbar nào.
   *
   * Đổi tay tốn đúng một khung hình (dispatch SELECT rồi trả về true): khung
   * sau `s.busy` vẫn bằng 0 nên nó vào lại đây và ra tay ngay. Cố ý không gộp
   * hai việc vào một khung — hotbar phải kịp vẽ lại, nếu không người chơi thấy
   * nhân vật cày bằng cái bình tưới.
   */
  function autoStep(s: GameState): boolean {
    /* Tìm việc quanh NEO trước — neo là ô vừa làm xong. Chỉ khi quanh đó hết
       việc mới bỏ neo và tìm lại từ chỗ nhân vật đang đứng.

       Không có neo thì mỗi lần đi múc nước xong là nó tìm việc quanh cái
       giếng, làm dở một góc ruộng khác, rồi lần sau lại nhảy chỗ nữa — bật tự
       động lên một lúc là cả nông trại lỗ chỗ, không lô nào xong. */
    let job = autoAnchor ? autoJob(s, content, autoRadius(s), autoAnchor) : null;
    if (!job) {
      autoAnchor = null;
      job = autoJob(s, content, autoRadius(s));
    }

    /* MÚC NƯỚC nằm giữa TƯỚI và CÀY trong thứ tự ưu tiên, không phải ở cuối.
       Bình cạn thì `canUseAt` bảo không tưới được, nên `autoJob` tụt xuống bậc
       CÀY và cày mãi cho tới hết sức — đúng thứ đã đo được: 51 ô cày thêm,
       0 ô tưới, ruộng vẫn khô. Đi múc mới là việc đáng làm lúc đó. */
    if (
      (!job || job.kind === "till") &&
      s.water <= 0 &&
      needsWater(s)
    ) {
      const w = nearestRefill(s);
      if (w) {
        workGoal = { x: w.x, y: w.y, refill: true };
        aimed = ghiNhoNgam(aimed, { x: w.x, y: w.y }, false);
        return nav.goTo(s, content, w.x, w.y, {});
      }
    }
    if (!job) return false;
    /* Chuyến do nút A nhận thì DỪNG khi hết đúng loại việc ấy. Không có dòng
       này thì đổ đầy ba cái máng xong, nhân vật đi cày tiếp — một việc người
       chơi không hề bảo, và họ phải bấm lại để bắt nó thôi. */
    if (autoOnly && job.kind !== autoOnly) return false;

    if (job.slot !== s.sel) {
      store.dispatch({ t: "SELECT", slot: job.slot });
      return true;
    }
    aimed = ghiNhoNgam(aimed, { x: job.x, y: job.y }, false);
    // Neo bám theo ô vừa chọn: nó trôi dần theo luống đang làm, và sau mỗi
    // chuyến đi múc nước thì đây là chỗ phải quay về.
    autoAnchor = { x: job.x, y: job.y };
    if (inReachOf(s, job.x, job.y)) return tryUse(s, job.x, job.y);
    workGoal = { x: job.x, y: job.y };
    return nav.goTo(s, content, job.x, job.y, { avoidStandingOn: holdingSolidBuilding(s) });
  }

  /** Còn ô đã cày mà khô không? Nếu không thì đừng bắt nhân vật lội bộ đi múc. */
  function needsWater(s: GameState): boolean {
    for (const t of s.tiles) if (t?.tilled && !t.wet) return true;
    return false;
  }


  /** Thu sản phẩm ở ô con vật đang đứng — `pressPlan` đã chọn đúng con. */
  function doGather(x: number, y: number): boolean {
    // Ghi `lastUse` để hạt của bus phản hồi rơi ĐÚNG con vật, không phải ô
    // cuốc lần cuối.
    lastUse = { x, y };
    store.dispatch({ t: "GATHER", x, y });
    return true;
  }

  /* ---- CHUYẾN CỦA MÓN ĐANG CẦM --------------------------------------
     Luật Cường đặt: "đang chọn cái gì [ở hotbar] có thể làm được ở khu vực nào
     thì phải di chuyển về khu vực đó để tiến hành làm, lặp lại chuyện đó,
     không có tự ý thay đổi công cụ." Bấm nút chính MỘT lần: làm hết việc của
     món đang cầm, khu nào gọn khu đó, dừng khi hết việc / hết món / hết sức,
     hoặc bấm lại. Chọn ô và khu nằm ở `game/run.ts` (thuần, test được); ở đây
     chỉ có vòng đời: bắt đầu, đi, dùng, dừng.

     Cố ý không lưu vào save, cùng lý do với `autoWork`. */

  /** Mọi thứ một nhát thành công có thể đổi. Đủ rộng để bao cả đổ máng, cho
   *  cá ăn (không có bộ đếm riêng) — năng lượng tụt là bằng chứng. */


  /**
   * Bắt đầu chuyến cho món đang cầm. Món không có chuyến (cầm đá, cầm công
   * trình) thì lắc đầu. Bước đầu chạy NGAY để bấm là có phản hồi — hết việc
   * thì toast liền, không đợi một khung hình.
   */

  /** Một bước của chuyến: hỏi ô kế, trong tầm thì làm, xa thì đi. */

  /** Tuỳ chọn cho `pressPlan` — MỘT chỗ quyết định, HUD và cú bấm cùng dùng. */
  function pressOpts(only?: "use" | "gather" | "interact" | "boat" | "any") {
    return { context: settings.contextButton, canGo: settings.contextButton && aimed !== null, only };
  }

  /**
   * Ô mà nút chính đang nói về — cùng một ô cho HUD lẫn cú bấm.
   *
   * KHÔNG hỏi `nav.target()`, và đó là cả chỗ sửa của Đợt 33.
   *
   * Trước đây ô ĐÍCH của chuyến đi được trả về TRƯỚC TIÊN, mà `goal` sống suốt
   * chuyến. Trong game bấm-để-đi thì "đang đi" là phần lớn thời gian, nên mũi
   * tên đỏ bị ghim vào ô đích và: bấm MỤC TIÊU đổi `aimed` thật nhưng con trỏ
   * bỏ qua (nút trông như chết) · đi ngang cái máng khác cũng không đổi · luật
   * rơi `aimStillValid` chỉ áp lên `aimed` chứ không áp lên ô của chuyến đi,
   * nên mũi tên cắm ở ô cách cả chục ô. Cường báo đúng cả ba.
   *
   * Nó đi ngược chính thiết kế Đợt 27, thứ đã tách BA DẤU BA NGHĨA: khung
   * trắng = sẽ đi đây · vòng vàng = đang trên đường tới · MŨI TÊN ĐỎ = nút
   * chính sẽ tác động vào đây. Trộn dấu thứ nhất vào dấu thứ ba là bỏ mất hai
   * câu trả lời để lấy một.
   *
   * Gỡ đi KHÔNG mất tính năng "chạm ô xa → đi tới rồi làm": tính năng ấy không
   * đi qua đây mà đi qua nấc 6 của `pressPlan` (`contextAction` → `goTo`), và
   * ba nhánh `goTo` trong đó cố ý KHÔNG hỏi `canGo`. Đích chuyến đi cũng đã có
   * dấu riêng rồi (khung trắng + vòng vàng), không cần mượn mũi tên đỏ.
   */
  function pressCursor(s: GameState): { x: number; y: number } | null {
    const c = targetTile(s);
    return c ? { x: c.x, y: c.y } : null;
  }

  /**
   * THỰC THI một cú bấm nút chính. Đây là chỗ DUY NHẤT nút chính làm việc;
   * mọi quyết định "làm gì" đã nằm trong `pressPlan` (src/game/hint.ts) — hàm
   * này chỉ chuyển từng nhánh thành dispatch/menu/đi tới.
   */
  function execute(s: GameState, p: Press): boolean {
    switch (p.t) {
      case "deny":
        deny();
        return false;
      case "build":
        buildUI.open();
        return true;
      /* MỘT cú bấm, MỘT nhát. Ở đây từng có: nhát nào cùng loại với "chuyến của
         món đang cầm" thì làm xong là tự nối sang nhát kế, nên bấm một lần để
         cày một ô là cày hết lô rồi nhổ cỏ luôn. Cường: "nó nhảy tùm lum mà nó
         cứ làm tự động thôi". Muốn làm hàng loạt thì bật "Tự động làm" trong
         menu — một đường riêng, người chơi tự chọn, tắt được. */
      case "use":
        return tryUse(s, p.x, p.y);
      case "gather":
        return doGather(p.x, p.y);
      case "interact":
        return doInteract(p.kind, p.x, p.y);
      case "boat":
        buzz("tap");
        menus.openShopTab("boat");
        return true;
      case "go": {
        workGoal = { x: p.x, y: p.y, then: p.then };
        if (nav.goTo(s, content, p.x, p.y, { avoidStandingOn: holdingSolidBuilding(s) })) return true;
        workGoal = null;
        // Trước đây thất bại ở đây là im lặng — nhân vật đứng yên, nút vẫn sáng.
        toasts.say("Không có đường tới đó", "bad");
        deny();
        return false;
      }
    }
  }

  /* Vòng tiêu điểm vàng chỉ có nghĩa khi tiêu điểm do BÀN PHÍM hoặc TAY CẦM
     đặt. Bấm chuột xong mà nút vẫn đeo vòng vàng thì trông như bị kẹt. */
  document.addEventListener("pointerdown", () => document.body.classList.add("pointer-focus"), true);
  document.addEventListener("keydown", () => document.body.classList.remove("pointer-focus"), true);

  /* ---- THANH CHỈ DẪN NÚT ------------------------------------------------
     Overlay nhỏ nói ĐÚNG những nút dùng được NGAY LÚC NÀY.

     Vì sao không chỉ có sơ đồ nút một lần lúc cắm: người chơi đọc bảng đó xong
     là quên, và nó cũng không nói được điều quan trọng nhất — cùng một nút vai
     đổi ô hotbar ngoài ruộng nhưng đổi TAB khi đang mở cửa hàng, cùng một nút
     mặt dưới là "cày" ngoài ruộng nhưng là "chọn" trong menu. Thanh này đổi
     theo ngữ cảnh nên nó luôn đúng, và người chơi không phải nhớ gì cả. */
  const padBar = $("#padbar");
  /** Dấu vân tay lần vẽ trước — thanh này được hỏi mỗi khung hình. */
  let padBarKey = "";

  function drawPadBar(inMenu: boolean, inBuild: boolean, inTut: boolean): void {
    if (!padOn) {
      if (padBarKey !== "") {
        padBarKey = "";
        padBar.hidden = true;
        padBar.innerHTML = "";
      }
      return;
    }
    const pi = input.padInfo();
    const b = (i: number) => padButtonName(pi, i);
    const std = pi.standard;

    const hints: [string, string][] = [];
    /* NÚT ĐÓNG là X (chỉ số 2), không còn là B. B mang việc TRA CỨU từ đợt
       này — in "Đóng" lên nó là dạy sai đúng cái nút người chơi bấm nhiều
       nhất khi muốn thoát. */
    if (inTut) {
      hints.push([b(0), "Tiếp"], [b(2), "Bỏ qua"]);
    } else if (inMenu) {
      hints.push(["✛", "Chuyển"], [b(0), "Chọn"], [b(2), "Đóng"]);
      if (std && menus.hasTabs()) hints.push([`${b(4)}/${b(5)}`, "Đổi tab"]);
    } else if (minimap.cursor()) {
      hints.push(["Cần phải", "Rê"], [b(0), "Đi tới đó"], [b(8), "Thôi"]);
    } else if (inBuild) {
      hints.push(["Cần phải", "Rê ô"], [b(0), lineFrom ? "Xây" : "Đặt mốc"], [b(2), lineFrom ? "Huỷ" : "Thoát"]);
      if (std) hints.push([`${b(4)}/${b(5)}`, "Đổi công trình"]);
    } else if (cardAnimal !== null) {
      hints.push([b(2), "Đóng bảng"], [b(0), "Thu / cho ăn"]);
      if (std) hints.push([`${b(4)}/${b(5)}`, "Đổi con"]);
    } else {
      /* Ngoài ruộng thì nút A đã tự nói việc của nó (nhãn trên chính cái nút),
         nên thanh này chỉ còn những nút vừa CÓ TÁC DỤNG ngay lúc này vừa không
         hiện ở đâu khác. Đủ ngắn để liếc một cái là đọc xong. */
      if (std)
        hints.push([b(1), "Tra cứu"], [b(3), "Balo"], [b(6), "Chạy"], [b(7), "Phóng"], [b(10), "Xây dựng"]);
      else hints.push([b(0), "Làm"], [b(1), "Tra cứu"], [b(2), "Quay lại"]);
    }

    /* Khi thanh này nói về một LỚP PHỦ (menu, hướng dẫn, bản đồ nhỏ, chế độ
       xây, bảng con vật) thì dải `.padctx` ở góc phải TẮT HẲN. Hai dải cùng
       hiện là hai chỉ dẫn khác nhau cho cùng một nút A: dải góc nói "DÙNG"
       trong khi thanh giữa nói "Chọn", và người chơi phải tự đoán cái nào đang
       thật. Trước đây dải góc chỉ MỜ ĐI (opacity .45) — mờ vẫn là đang nói. */
    document.body.dataset["padbar"] =
      inTut || inMenu || inBuild || cardAnimal !== null || minimap.cursor() ? "1" : "";

    const key = hints.map(([k, v]) => k + v).join("|");
    if (key === padBarKey) return;
    padBarKey = key;
    padBar.innerHTML = "";
    for (const [k, v] of hints) {
      const wrap = document.createElement("span");
      const kb = document.createElement("b");
      kb.textContent = k;
      const t = document.createElement("i");
      t.textContent = v;
      wrap.append(kb, t);
      padBar.appendChild(wrap);
    }
    padBar.hidden = false;
  }

  /** Dòng nhật ký cuối cùng đã xét cho động tác lắc đầu — xem chỗ đọc `s.log`. */
  let logDaLac = 0;
  const deny = () => {
    play("deny");
    buzz("deny");
    // Và nhân vật LẮC ĐẦU. Một dòng chữ trôi ở góc màn hình với một tiếng "bụp"
    // là hai thứ dễ bỏ lỡ nhất; cái lắc đầu xảy ra ngay chỗ mắt đang nhìn.
    renderer.refuse();
  };

  /**
   * Chuyển tiêu điểm sang phần tử bấm được GẦN NHẤT theo hướng (dx,dy).
   *
   * Chọn theo hình học thay vì theo thứ tự DOM vì menu xếp LƯỚI: đi theo thứ
   * tự DOM thì gạt sang phải ở nút cuối hàng lại nhảy xuống đầu hàng dưới —
   * đúng nhưng không phải thứ mắt đang nhìn thấy.
   *
   * Chấm điểm = khoảng cách dọc theo hướng + phần lệch NGANG nhân bốn. Nhân
   * bốn để một nút thẳng hàng ở xa vẫn thắng một nút lệch hàng ở gần; không có
   * hệ số đó thì gạt xuống trong lưới hai cột hay nhảy chéo sang cột kia.
   */
  /**
   * Khung đang nhận tiêu điểm, theo đúng thứ tự LỚP TRÊN CÙNG.
   *
   * Hỏi từng lớp một chứ KHÔNG dùng một selector gộp: `querySelector` với danh
   * sách chọn trả về phần tử đầu tiên theo THỨ TỰ DOM, không theo thứ tự viết
   * trong selector. Mà trong `farm/index.html` thì `#devpanel` (dòng 32) nằm
   * TRƯỚC `#modal-root` (dòng 43) — nên mở bảng gỡ lỗi rồi mở menu Tạm dừng
   * thì D-pad vẫn lái cái bảng gỡ lỗi, còn menu vừa mở thì đứng im.
   *
   * Ba lớp cuối là các bảng nổi ngoài modal: chúng có nút thật mà trước đây
   * nằm ngoài tầm với của tay cầm — thanh báo bản mới là chỗ đáng tiếc nhất,
   * vì nút duy nhất trên đó là nút CẬP NHẬT.
   */
  function focusRoot(): HTMLElement | null {
    const q = (sel: string) => document.querySelector<HTMLElement>(sel);
    return (
      q(".tut-card") ??
      q(".modal") ??
      q("#devpanel:not([hidden])") ??
      q("#buildmode:not([hidden]) .bm-pal") ??
      q("#update-bar:not([hidden])") ??
      q("#animal-card:not([hidden])")
    );
  }

  /**
   * Mọi thứ tay cầm bấm được trong khung này.
   *
   * KHÔNG chỉ `<button>`. Ô balo, ô kho, dòng quầy thu mua đều là
   * `<div role="button">` — trình duyệt không cho chúng nhận tiêu điểm, nên
   * D-pad đi qua chúng như thể chúng không tồn tại. Đó là lý do balo, cửa
   * hàng, kho và cài đặt "không bấm được bằng tay cầm" dù menu tạm dừng thì
   * được: menu tạm dừng toàn nút thật.
   *
   * Sửa ở ĐÂY chứ không sửa từng màn hình: mỗi màn mới viết sau này sẽ tự
   * chạy được, không phải nhớ thêm một luật. Tách khỏi `moveFocus` vì cả điều
   * hướng lẫn khôi phục đều phải hỏi đúng một câu hỏi này.
   */
  function ungVien(root: HTMLElement): HTMLElement[] {
    return [
      ...root.querySelectorAll<HTMLElement>(
        "button:not([disabled]), [role='button'], [role='option'], input:not([disabled]), select:not([disabled]), [tabindex]",
      ),
    ].filter((el) => el.offsetParent !== null && !el.hasAttribute("disabled"));
  }

  /**
   * Tâm phần tử trong hệ toạ độ BỐ CỤC của tấm menu.
   *
   * Vì sao KHÔNG dùng `getBoundingClientRect`: đúng ở cái khung hình cần đo,
   * tấm menu vừa dựng lại đang chạy hoạt cảnh `modal-in` / `sheet-in` nên toạ
   * độ màn hình của nó lệch tới 40px, mà thân menu thì vừa bị trả về đầu danh
   * sách. Hai thứ đó cộng lại quá đủ để nhận nhầm sang hàng bên cạnh (hàng cao
   * 52px). `offsetTop` là vị trí BỐ CỤC: không biết tới transform, không biết
   * tới cuộn — đúng hai thứ đang gây nhiễu.
   */
  function choNgoi(el: HTMLElement, root: HTMLElement): Seat {
    let x = 0;
    let y = 0;
    let n: HTMLElement | null = el;
    while (n && n !== root) {
      x += n.offsetLeft;
      y += n.offsetTop;
      n = n.offsetParent as HTMLElement | null;
    }
    return {
      x: x + el.offsetWidth / 2,
      y: y + el.offsetHeight / 2,
      kind: `${el.tagName}.${(el.parentElement?.className ?? "").trim()}`,
    };
  }

  /**
   * Định danh MÀN HÌNH. `shell()` dựng `.modal` mới mỗi lần vẽ nên không so
   * được bằng tham chiếu; `data-menu` là tiêu đề màn, một chuỗi HẰNG trong
   * từng `open*()` và khác nhau giữa các màn.
   */
  function khoaManHinh(root: HTMLElement): { screen: string; tab: string } {
    return {
      screen: `${root.id || root.classList[0] || root.tagName}|${root.dataset["menu"] ?? ""}`,
      tab: root.querySelector(".tabs button.on")?.textContent ?? "",
    };
  }

  /** Ghi lại chỗ ngồi hiện tại. Gọi ở CUỐI mỗi khung hình — tức là ngay trước
   *  cú bấm sẽ dựng lại menu. */
  /** Dấu vân tay lần ghi nhớ tiêu điểm gần nhất — xem chỗ gọi trong vòng vẽ. */
  let nhoAe: Element | null = null;
  let nhoScroll = -1;
  let nhoRoot: HTMLElement | null = null;
  function nhoTieuDiem(root: HTMLElement): void {
    const el = document.activeElement as HTMLElement | null;
    const trong = !!el && el !== root && root.contains(el);
    const k = khoaManHinh(root);
    focusMemo = {
      screen: k.screen,
      tab: k.tab,
      scroll: root.querySelector<HTMLElement>(".body")?.scrollTop ?? 0,
      at: trong ? choNgoi(el, root) : null,
    };
  }

  function moveFocus(dx: number, dy: number): void {
    const root = focusRoot();
    if (!root) return;
    const all = ungVien(root);
    if (!all.length) return;

    const cur = document.activeElement as HTMLElement | null;
    if (!cur || !root.contains(cur)) {
      focusIn(all[0]!);
      return;
    }
    const a = cur.getBoundingClientRect();
    const ax = a.left + a.width / 2;
    const ay = a.top + a.height / 2;

    let best: HTMLElement | null = null;
    let bestScore = Infinity;
    for (const el of all) {
      if (el === cur) continue;
      const b = el.getBoundingClientRect();
      const bx = b.left + b.width / 2;
      const by = b.top + b.height / 2;
      const doc = (bx - ax) * dx + (by - ay) * dy; // đi tới bao xa theo hướng
      if (doc <= 2) continue; // ở phía sau hoặc ngang hàng: bỏ qua
      const lech = Math.abs((bx - ax) * dy) + Math.abs((by - ay) * dx);
      const score = doc + lech * 4;
      if (score < bestScore) {
        bestScore = score;
        best = el;
      }
    }
    focusIn(best ?? all[0]!);
  }

  /**
   * Chuyển bảng sang con vật KẾ TIẾP trên bản đồ, theo thứ tự khoảng cách tới
   * nhân vật. Trả false nếu không có con nào.
   *
   * Xếp theo khoảng cách chứ không theo id: id là thứ tự MUA, mà người chơi
   * nghĩ theo "con bên cạnh", không nghĩ theo "con tôi mua thứ ba".
   */
  function cycleAnimal(s: GameState, d: number): boolean {
    const list = s.entities
      .filter((e) => (e.kind === "animal" || e.kind === "worker") && e.map === s.mapId)
      .sort(
        (a, b) =>
          Math.hypot(a.x - s.player.x, a.y - s.player.y) -
          Math.hypot(b.x - s.player.x, b.y - s.player.y),
      );
    if (!list.length) return false;
    const i = list.findIndex((e) => e.id === cardAnimal);
    const n = list.length;
    cardAnimal = list[((((i < 0 ? 0 : i) + d) % n) + n) % n]!.id;
    buzz("tap");
    return true;
  }

  /** Đặt tiêu điểm VÀ kéo nó vào tầm nhìn. Cửa hàng có bốn mươi thẻ hạt cuộn
   *  dọc; chuyển tiêu điểm xuống thẻ thứ ba mươi mà không cuộn theo thì màn
   *  hình đứng yên và người chơi tưởng cần gạt hỏng. */
  function focusIn(el: HTMLElement, giuCuon = false): void {
    /* `tabIndex = -1` cho phép GỌI `.focus()` mà không nhét phần tử vào thứ tự
       phím Tab — đúng thứ cần cho `div role="button"`: tay cầm tới được, còn
       người dùng bàn phím vẫn Tab qua đúng các nút thật. */
    if (!el.hasAttribute("tabindex") && el.tagName !== "BUTTON" && el.tagName !== "INPUT")
      el.tabIndex = -1;
    /* Trên nhánh KHÔI PHỤC thì chỗ cuộn vừa được trả về đúng chỗ cũ rồi — cuộn
       thêm lần nữa chỉ để nó nhích vài pixel dưới ngón tay người chơi. */
    el.focus({ preventScroll: giuCuon });
    if (!giuCuon) el.scrollIntoView({ block: "nearest", inline: "nearest" });
  }

  /**
   * Đổi TAB trong menu bằng LB/RB.
   *
   * Cửa hàng chia bốn tab, mà tab là những nút nằm trên cùng — với tay cầm thì
   * phải gạt lên mấy lần mới tới, rồi lại gạt xuống mấy lần để về chỗ cũ. Vai
   * là chỗ đúng cho việc đó, y như mọi game có tab.
   */
  function cycleTab(d: number): boolean {
    const root = focusRoot();
    const tabs = root ? [...root.querySelectorAll<HTMLButtonElement>(".tabs button")] : [];
    if (tabs.length < 2) return false;
    const i = tabs.findIndex((b) => b.classList.contains("on"));
    const n = tabs.length;
    tabs[(((i < 0 ? 0 : i) + d) % n + n) % n]!.click();
    return true;
  }

  const vpTiles = () => ({
    w: camera.viewport.viewW / TILE,
    h: camera.viewport.viewH / TILE,
  });

  /* ---- 10. vòng lặp ---- */
  let elapsed = 0;
  /* Một khung hình ném lỗi KHÔNG được làm đứng hình vĩnh viễn — xem loop.ts.
     Lần đầu: nói một câu và LƯU NGAY (reducer thuần, nên state trong store vẫn
     là state tốt cuối cùng — lỗi đã ném trước khi nó được gán). Lỗi ở mọi khung
     liên tiếp thì dừng hẳn và nói thật, vì chạy tiếp chỉ là quay tròn. */
  let daBaoLoiKhung = false;
  const khiLoiKhungHinh = (e: unknown, lienTiep: number): boolean => {
    console.error("[oni-farm] lỗi trong khung hình", e);
    if (!daBaoLoiKhung) {
      daBaoLoiKhung = true;
      toasts.say("Game gặp lỗi — đã lưu lại ván của anh.", "bad");
      void autosave();
    }
    if (lienTiep < MAX_CONSECUTIVE_ERRORS) return true;
    showError(
      e instanceof Error ? (e.stack ?? e.message) : String(e),
      "Game gặp lỗi liên tục và phải dừng. Ván chơi đã được lưu — tải lại trang để chơi tiếp.",
    );
    return false;
  };

  const loop = createLoop((dt) => {
    elapsed += dt;
    // Tay cầm là hệ HỎI VÒNG, không phát sự kiện — phải đọc đúng một lần mỗi
    // khung hình, TRƯỚC khi ai đó hỏi `axis()` hay `drain()`.
    input.poll(performance.now());

    /* ---- TỰ NHẬN TAY CẦM -------------------------------------------------
       `body[data-input="pad"]` để CSS giấu joystick ảo và các nút chạm: cầm tay
       cầm rồi thì mấy thứ đó chỉ che mất nông trại.

       Vì sao phải POLL chứ không chỉ nghe sự kiện `gamepadconnected`: vì lý do
       chống nhận diện, Chrome và Safari chỉ báo sự kiện đó SAU khi người chơi
       bấm một nút. Cắm tay cầm rồi ngồi im thì không có sự kiện nào cả — nhưng
       `poll()` mỗi khung hình thì thấy ngay lúc nút đầu tiên được bấm. */
    /* `padOn` = ĐANG CHƠI bằng tay cầm, không phải "có tay cầm cắm". Dải gợi ý
       nút và con trỏ ô của chế độ xây đều đọc nó, và cả hai chỉ đúng khi người
       chơi thật sự cầm tay cầm — cắm vào để đó mà đã đổi cả HUD là thứ Cường
       gặp phải. */
    const coPad = input.mode() === "pad";
    if (coPad !== padOn) {
      padOn = coPad;
      /* KHÔNG ghi `data-input` ở đây nữa: cắm tay cầm vào không có nghĩa là
         đang dùng nó. `veCheDo` mới là nơi ghi, và nó chạy khi chế độ thật sự
         đổi. Khối này chỉ còn lo phần "chào tay cầm": dán tên nút thật, một
         câu toast, và bảng sơ đồ nút lần đầu. */
      veCheDo();
      if (coPad) {
        const pi = input.padInfo();
        /* Dán TÊN NÚT THẬT lên từng nút tròn. Cụm hình thoi trên màn hình xếp
           đúng như mặt tay cầm, nên gắn tên vào là nó thành bản đồ nút: nhìn
           một cái là biết ngón cái phải bấm chỗ nào, khỏi phải nhớ. */
        for (const [sel, i] of [
          ["#abtn .a", 0],
          ["#abtn .b", 1],
          ["#abtn .y", 3],
        ] as [string, number][]) {
          const el = document.querySelector<HTMLElement>(sel);
          // Chỉ dán khi tay cầm có nút đó VÀ trình duyệt nhận ra sơ đồ chuẩn:
          // dán "X" lên một nút mà bấm X không ra gì là chỉ dẫn sai.
          if (el) {
            if (pi.standard && i < pi.buttons) el.dataset["pad"] = padButtonName(pi, i);
            else delete el.dataset["pad"];
          }
        }
        const mo = pi.standard ? padButtonName(pi, 11) : padButtonName(pi, 0);
        toasts.say(`Đã nhận tay cầm — bấm ${mo} để xem sơ đồ nút.`, "good");
        // `buzz` giờ rung CẢ tay cầm (xem `setPadRumble`), nên không gọi
        // `input.rumble` riêng nữa — gọi riêng là đi vòng qua công tắc Rung.
        buzz("heavy");
        // Bảng nút chỉ tự mở LẦN ĐẦU trên máy này. Mở lại mỗi lần cắm là phiền,
        // mà không mở lần nào thì người chơi không bao giờ biết Y mở cửa hàng.
        try {
          if (!localStorage.getItem(PAD_SEEN)) {
            localStorage.setItem(PAD_SEEN, "1");
            if (!menus.isOpen() && !tutorial.isOpen()) menus.openPadHelp();
          }
        } catch {
          /* localStorage bị chặn — thôi không mở tự động, không phải lỗi */
        }
      }
    }

    const modal = menus.isOpen() || tutorial.isOpen();
    /* Chế độ xây dựng KHÔNG phải modal: người chơi vẫn đi lại được để ngắm chỗ
       xây, chỉ có ĐỒNG HỒ là đứng. Gộp nó vào `modal` thì nhân vật cứng đơ và
       không xem được góc bên kia nông trại. */
    const building = buildUI.isOpen();

    if (modal) {
      nav.cancel();
    }
    if (building) {
      nav.cancel();
      if (autoWork) setAuto(false);
    }

    /* Đang RÊ CON TRỎ Ô (chế độ xây, hoặc con trỏ bản đồ nhỏ) thì cần gạt lái
       CON TRỎ, không lái nhân vật. Trước đây một cú đẩy làm cả hai cùng lúc —
       nhân vật trôi liên tục còn con trỏ nhảy theo nhịp lặp — hai tốc độ khác
       nhau dưới cùng một ngón cái, và không cách nào ngắm cho đứng. */
    const reOChuot = padOn && (building || !!minimap.cursor());

    /* MỤC TIÊU tự rơi khi ra khỏi TẦM VỚI — luật duy nhất quyết định nó sống
       hay chết, và nó nằm ở ĐÂY chứ không ở chỗ đọc. Trước Đợt 27 luật này chỉ
       tồn tại ngầm trong `targetTile`, nên trạng thái thật không bao giờ quan
       sát được và không kịch bản nào kiểm được nó.

       Cố ý KHÔNG xoá khi người chơi bước đi (bản cũ làm thế, hồi `aimed` còn
       nghĩa là "ô vừa chạm"): mục tiêu vừa chọn mà biến mất ngay bước chân đầu
       tiên thì nút MỤC TIÊU vô dụng. */
    if (!aimStillValid(store.getState(), aimed)) {
      aimed = null;
      chuotLucChon = null;
    }

    if (!modal) {
      const ax = reOChuot ? { x: 0, y: 0 } : input.axis();
      if (ax.x !== 0 || ax.y !== 0) {
        // Người chơi tự cầm lái thì nhường ngay — cùng luật với `nav.cancel()`:
        // nhập tay luôn thắng thứ đang chạy tự động.
        if (autoWork) setAuto(false);
          nav.cancel();
        store.dispatch({ t: "MOVE", dx: ax.x, dy: ax.y, dt, run: input.running() });
      } else {
        const step = nav.update(store.getState(), content, dt);
        if (step) store.dispatch({ t: "MOVE", dx: step.dx, dy: step.dy, dt, run: step.run });
        else if (store.getState().player.moving)
          store.dispatch({ t: "MOVE", dx: 0, dy: 0, dt });
      }
      /* Đây là chỗ THỜI GIAN TRÔI. Không dispatch TICK nghĩa là: cây không lớn,
         con vật không đói, xe không chạy, đồng hồ đứng. Đúng ý của chế độ xây
         dựng — quy hoạch là lúc ngồi nhìn và nghĩ, mà đồng hồ chạy trong lúc
         nghĩ thì biến việc nghĩ thành việc mất mát. */
      if (!building) store.dispatch({ t: "TICK", dt });

      const arrived = nav.takeArrival();
      if (arrived) {
        aimed = ghiNhoNgam(aimed, { x: arrived.tx, y: arrived.ty }, false);
        const goal =
          workGoal !== null && workGoal.x === arrived.tx && workGoal.y === arrived.ty
            ? workGoal
            : null;
        workGoal = null;
        if (arrived.act) {
          const st = store.getState();
          /* Chuyến đi MÚC NƯỚC là ngoại lệ duy nhất được phép tương tác: nó
             phải mở được cái giếng. Mọi chuyến đi làm việc khác vẫn chỉ được
             `tryUse` — đi cày một ô cạnh quầy thu mua mà bật hộp thoại bán hàng
             giữa lúc tự động làm là thế giới đứng hình cho tới khi tắt nó đi. */
          const only = goal?.refill ? "interact" : (goal?.then ?? "any");
          const p = pressPlan(st, content, { x: arrived.tx, y: arrived.ty }, pressOpts(only));
          if (p.t === "go") deny();
          else execute(st, p);
        }
      }

      // TỰ ĐỘNG LÀM: cùng một hàm với giữ-nút, chỉ khác là không cần giữ. Chờ
      // hết `busy` và hết đường đang đi rồi mới chọn việc mới, nên vẫn TUẦN TỰ
      // từng việc một như Cường muốn.
      if (autoWork && !building) {
        const s = store.getState();
        const mark = progressMark(s);
        if (mark !== autoMark) {
          autoMark = mark;
          autoIdle = 0;
        } else if (nav.target()) {
          /* Đang ĐI TỚI chỗ làm thì không tính là đứng không. Bán kính tự động
             là 12 ô, mà đi hết 12 ô còn lâu hơn ngưỡng bốn giây — không loại
             trừ thì cứ mỗi lần việc ở xa là tự tắt giữa đường. */
          autoIdle = 0;
        } else if ((autoIdle += dt) > AUTO_IDLE_LIMIT) {
          stopAuto(s);
        }
        if (autoWork && s.busy <= 0 && !nav.target()) {
          if (!autoStep(s)) autoMiss++;
          else autoMiss = 0;
          if (autoMiss >= 2) stopAuto(store.getState());
        }
      }

    }

    for (const it of input.drain()) {
      const s = store.getState();
      switch (it.t) {
        /* Escape / Start là nút LÙI MỘT LỚP, nên nó phải gỡ đúng lớp trên
           cùng. Thiếu `devPanel` ở đây thì mở bảng gỡ lỗi rồi bấm Escape lại
           bật thêm menu Tạm dừng chồng lên nó — lùi mà lại tiến. */
        /* MENU (Escape, nút ☰, START) đi cùng đường với QUAY LẠI: đóng thứ đang
           mở từ nông tới sâu, hết rồi mới bật menu Tạm dừng. Trước đây nhánh
           này chỉ biết tutorial/menus/devpanel, nên Escape trong CHẾ ĐỘ XÂY mở
           menu Tạm dừng ĐÈ LÊN chế độ xây, và người chơi bàn phím không có phím
           nào đóng được bảng con vật. */
        case "menu":

        /* QUAY LẠI (nút B trên tay cầm).
           Một thứ tự duy nhất, từ nông tới sâu: đóng cái đang mở; không có gì
           mở thì bật menu tạm dừng. Người chơi không phải nhớ nút nào đóng cái
           gì — cứ bấm B là lùi ra một lớp, và lùi hết thì ra menu. */
        case "back":
          if (tutorial.isOpen()) tutorial.close();
          else if (menus.isOpen()) menus.close();
          else if (devPanel.isOpen()) devPanel.close();
          else if (minimap.cursor()) minimap.setCursor(null);
          else if (building) buildUI.close();
          else if (cardAnimal !== null) cardAnimal = null;
          else menus.openPause();
          break;

        case "shop":
          if (!modal) menus.openShop();
          break;
        case "inventory":
          if (!modal) menus.openBag();
          else if (menus.isOpen()) menus.close();
          break;
        /* Bản đồ nhỏ. Với chuột/ngón tay thì nút này chỉ bật/tắt hiện — bấm
           thẳng vào bản đồ là đi. Tay cầm không có con trỏ, nên nó bật thêm
           một CON TRỎ Ô: gạt để rê, A để đi. Không có đường này thì bấm-để-đi
           (cách đi xa duy nhất) hoàn toàn ngoài tầm tay cầm, và người chơi
           phải giữ cần gạt suốt chiều dài nông trại. */
        /* CÒ PHẢI = đổi mức phóng. Trước đây cò phải làm ĐÚNG việc của A, còn
           mức phóng thì chỉ mở được trong Cài đặt — nghĩa là một nút thừa và
           một tính năng không với tới được, cùng một lúc. */
        case "zoom": {
          if (modal || building) break;
          const muc: Settings["zoom"][] = ["near", "normal", "far"];
          const i = Math.max(0, muc.indexOf(settings.zoom));
          const moi = muc[(i + 1) % muc.length]!;
          setSetting("zoom", moi);
          toasts.say(moi === "near" ? "Khung nhìn: gần" : moi === "far" ? "Khung nhìn: xa" : "Khung nhìn: vừa");
          buzz("tap");
          break;
        }

        case "map":
          if (padOn && !modal) {
            if (minimap.cursor()) {
              minimap.setCursor(null);
              if (!minimap.isVisible()) minimap.toggle();
            } else {
              if (!minimap.isVisible()) minimap.toggle();
              minimap.setCursor({
                x: Math.floor(s.player.x / TILE),
                y: Math.floor(s.player.y / TILE),
              });
            }
          } else minimap.toggle();
          break;
        case "debug":
          devPanel.toggle();
          break;
        case "auto":
          // Nút AUTO (cảm ứng) = chế độ ĐẦY ĐỦ, không giới hạn loại việc.
              setAuto(!autoWork, null);
          toasts.say(autoWork ? "Tự động làm: BẬT" : "Tự động làm: TẮT", "info");
          break;
        case "select":
          // Đổi món là đổi ý — chuyến của món cũ dừng, KHÔNG tự cầm lại.
              store.dispatch({ t: "SELECT", slot: it.slot });
          break;
        case "selectDelta": {
          /* Đang mở BẢNG VẬT NUÔI thì vai là nút ĐỔI CON, không phải đổi ô
             hotbar. Xem một con rồi muốn xem con kế bên là việc làm liên tục —
             bắt đi tới tận nơi rồi bấm lại từng con là việc của người, không
             phải của một cái bảng tra cứu. */
          if (!modal && cardAnimal !== null && cycleAnimal(s, it.d)) break;
          /* Đang XÂY thì vai đổi CÔNG TRÌNH. Thanh gợi ý nút đã in dòng này từ
             trước (xem `drawPadBar`) mà không có nhánh nào thực hiện — nên nó
             rơi xuống tận nhánh cuối và đổi ô hotbar, tức là chỉ dẫn trên màn
             hình nói một đằng, máy làm một nẻo. */
          if (!modal && building) {
            buildUI.cycle(it.d);
            break;
          }
          // Đang mở menu thì vai là nút ĐỔI TAB, không phải đổi ô hotbar.
          if (modal) {
            if (!cycleTab(it.d)) focusRoot()?.querySelector(".body")?.scrollBy({ top: it.d * 120 });
            break;
          }
          const n = content.balance.hotbarSlots;
              store.dispatch({ t: "SELECT", slot: (s.sel + it.d + n) % n });
          break;
        }
        case "use": {
          /* Con trỏ BẢN ĐỒ NHỎ đang bật: A là "đi tới đó", đúng như một cú
             chạm vào bản đồ. Xong thì tắt con trỏ — nó là một cử chỉ nhắm rồi
             bắn, không phải một chế độ phải nhớ tắt. */
          const mc0 = minimap.cursor();
          if (mc0 && !building) {
            minimap.setCursor(null);
            nav.goTo(s, content, mc0.x, mc0.y, { act: false });
            break;
          }
          /* Trong CHẾ ĐỘ XÂY DỰNG bằng tay cầm, A là "đặt mốc / chốt đoạn" —
             hai lần bấm thay cho một cú ấn-rê-nhả của ngón tay. */
          if (building && padOn) {
            const cur = padCursor ?? {
              x: Math.floor(s.player.x / TILE),
              y: Math.floor(s.player.y / TILE),
            };
            padCursor = cur;
            if (!lineFrom) {
              lineFrom = { ...cur };
              lineTo = { ...cur };
            } else {
              const bid = buildUI.picked();
              if (bid)
                store.dispatch({
                  t: "BUILD_LINE",
                  id: bid,
                  x0: lineFrom.x,
                  y0: lineFrom.y,
                  x1: cur.x,
                  y1: cur.y,
                  far: true,
                });
              lineFrom = null;
              lineTo = null;
            }
            break;
          }
          if (modal) break;
          /* Cầm công trình mà bấm DÙNG thì MỞ CHẾ ĐỘ XÂY DỰNG, không đặt xuống
             ô đang ngắm. Một cú bấm, và người chơi rơi vào đúng chỗ để quy
             hoạch — thay vì rắc từng ô rồi tự hỏi vì sao địa hình lởm chởm. */
          if (!building && heldBuilding(s)) {
            buildUI.open();
            break;
          }
          /* MỘT NGUỒN: `pressPlan` đã in nhãn lên nút; giờ nó nói làm gì.
             Không có phép quét nào ở đây nữa — mọi "con vật trước, công cụ
             trước tương tác, khu, chuyến" nằm trong src/game/hint.ts và được
             sim kiểm. */
          execute(s, pressPlan(s, content, pressCursor(s), pressOpts()));
          break;
        }
        /* CHẠM = ĐI. Không còn nghĩa nào khác.
           Cường, Đợt 27: "con trỏ này chỉ mục đích định hướng di chuyển".
           Mọi HÀNH ĐỘNG đi qua nút chính, và ô nó tác động vào thì mũi tên đỏ
           chỉ ra — nên không còn gì để chạm-hai-lần phân biệt. */
        case "pointer": {
          if (building) {
            /* Trong chế độ XÂY thì cú chạm là đặt điểm đầu của tuyến, và ở đó
               `snapTap` vẫn đúng: người chơi đang chỉ vào một Ô cụ thể để xây,
               không phải chỉ một chỗ để đi tới. `lineFrom/lineTo` là biến
               riêng — cố ý KHÔNG dùng chung `aimed`, vì `aimed` giờ mang nghĩa
               mục tiêu của nút chính và sẽ làm mũi tên đỏ hiện giữa lúc xây. */
            const q = snapTap(s, it.wx, it.wy);
            lineFrom = { x: q.x, y: q.y };
            lineTo = { x: q.x, y: q.y };
            break;
          }
          if (modal) break;
          /* Ô THÔ, không `snapTap`. Phép hút cú chạm về "ô có nghĩa gần nhất"
             sinh ra hồi cú chạm còn phải chọn ô để LÀM VIỆC; với nghĩa mới nó
             thành sai hẳn — chạm vào khoảnh cỏ trống cạnh gốc cây thì nhân vật
             lại đi sang ô gốc cây, tức là đi chỗ khác chỗ người chơi chỉ. */
          const tx = Math.max(0, Math.min(s.w - 1, Math.floor(it.wx / TILE)));
          const ty = Math.max(0, Math.min(s.h - 1, Math.floor(it.wy / TILE)));
          // Người chơi tự chạm thì đây KHÔNG còn là chuyến đi làm việc nữa —
          // tới nơi được phép mở cửa hàng/giường như bình thường. Chuyến đang
          // chạy cũng dừng: nhập tay luôn thắng thứ đang chạy tự động.
              workGoal = null;
          /* Chạm-để-đi là để ĐI. Từng mở bảng con vật khi chạm trong 1,4 ô
             quanh nó — mà 24 con đi khắp sân, nên gần như cú chạm nào cũng
             "mở bảng", và camera (hồi còn bám con vật đang mở bảng) bỏ nhân
             vật lại. Mở bảng giờ là việc của nút XEM và nút vai — có chủ ý. */
          cardAnimal = null;

          // Chạm lại đúng ô ĐANG đi tới thì để yên cho nhân vật đi tiếp.
          // Trước đây mọi cú chạm đều huỷ rồi tìm đường lại, nên người chơi
          // sốt ruột bấm dồn là nhân vật dừng-chạy-dừng-chạy — đúng cảm giác
          // "giật giật". Bấm lại chỗ cũ là XÁC NHẬN, không phải lệnh mới.
          const cur = nav.target();
          if (cur && cur.tx === tx && cur.ty === ty) break;
          nav.cancel();
          if (!inReachOf(s, tx, ty))
            nav.goTo(s, content, tx, ty, {
              act: false,
              avoidStandingOn: holdingSolidBuilding(s),
            });
          break;
        }

        /* NÚT MỤC TIÊU — thứ thay thế cho chạm-hai-lần.
           Danh sách và phép đi vòng nằm trong `src/game/hint.ts`, hỏi thẳng
           `pressPlan` nên mũi tên đỏ không bao giờ chỉ khác chỗ với thứ nút
           chính sẽ làm. Ở đây chỉ còn việc ghi nhớ và báo khi không có gì. */
        case "aimNext": {
          if (modal || building) break;
          const ds = reachTargets(s, content, pressOpts());
          const t = nextTarget(ds, aimed, it.d);
          if (!t) {
            aimed = null;
            deny();
            toasts.say("Không có gì trong tầm với", "info");
            break;
          }
          aimed = ghiNhoNgam(aimed, { x: t.x, y: t.y }, true);
          /* Ghim mốc chuột: xem `chuotLucChon`. Không ghim thì trên máy tính
             mục tiêu vừa chọn chết ngay ở khung hình sau. */
          chuotLucChon = input.pointer();
          buzz("tap");
          break;
        }
        /* Rê ngón/chuột khi đang vẽ tuyến: chỉ cập nhật ô CUỐI. Không dispatch
           gì cả — dựng dở từng ô theo đường rê thì rê lệch một cái là mất vật
           liệu, mà không có cách nào lấy lại. */
        case "drag": {
          if ((modal && !building) || !lineFrom) break;
          const q = snapTap(s, it.wx, it.wy);
          lineTo = { x: q.x, y: q.y };
          break;
        }

        case "dragEnd": {
          const id = building ? buildUI.picked() : null;
          if (id && lineFrom && lineTo)
            store.dispatch({
              t: "BUILD_LINE",
              id,
              x0: lineFrom.x,
              y0: lineFrom.y,
              x1: lineTo.x,
              y1: lineTo.y,
              far: building,
            });
          lineFrom = null;
          lineTo = null;
          break;
        }

        /* ---- ĐIỀU HƯỚNG MENU BẰNG TAY CẦM ----------------------------
           Menu là DOM, tay cầm không có con trỏ. Chọn phần tử kế tiếp theo
           HÌNH HỌC chứ không theo thứ tự trong DOM: menu xếp lưới hai cột, mà
           đi theo thứ tự DOM thì gạt sang phải lại nhảy xuống hàng dưới. */
        case "navDir":
          moveFocus(it.dx, it.dy);
          break;

        case "navScroll":
          focusRoot()?.querySelector(".body")?.scrollBy({ top: it.dy * 90 });
          break;

        case "build":
          if (!modal) buildUI.toggle();
          break;

        case "padHelp":
          if (!modal) menus.openPadHelp();
          break;

        /* Rê con trỏ ô bằng cần gạt. Chỉ có nghĩa trong chế độ xây dựng —
           ngoài đó thì cần gạt là để ĐI, và ngắm bám theo hướng mặt. */
        case "padAim": {
          /* Con trỏ BẢN ĐỒ NHỎ được ưu tiên: nó chỉ bật khi người chơi vừa
             chủ động bấm Back, nên lúc đó đó chắc chắn là thứ họ đang lái. */
          const mc = minimap.cursor();
          if (mc) {
            minimap.setCursor({
              x: Math.max(0, Math.min(s.w - 1, mc.x + it.dx)),
              y: Math.max(0, Math.min(s.h - 1, mc.y + it.dy)),
            });
            break;
          }
          if (building) {
            const p0 = padCursor ?? {
              x: Math.floor(s.player.x / TILE),
              y: Math.floor(s.player.y / TILE),
            };
            padCursor = {
              x: Math.max(0, Math.min(s.w - 1, p0.x + it.dx)),
              y: Math.max(0, Math.min(s.h - 1, p0.y + it.dy)),
            };
            // Đang vẽ dở thì đầu kia của đoạn bám theo con trỏ.
            if (lineFrom) lineTo = { ...padCursor };
            break;
          }

          /* NGOÀI RUỘNG: cần phải rê Ô NGẮM quanh nhân vật.
             Đây là thứ tay cầm vốn KHÔNG có: chuột chỉ vào đâu cũng được, ngón
             tay chạm vào đâu cũng được, còn tay cầm thì mãi mãi chỉ làm được ở
             đúng ô trước mặt — muốn cày ô chéo phải xoay người rồi bước. Giới
             hạn trong TẦM VỚI để nó vẫn là "ngắm", không thành một con chuột
             thứ hai. */
          {
            const a0 = aimed ?? facingTile(s, TILE);
            const nx = Math.max(0, Math.min(s.w - 1, a0.x + it.dx));
            const ny = Math.max(0, Math.min(s.h - 1, a0.y + it.dy));
            if (inReachOf(s, nx, ny)) {
              aimed = ghiNhoNgam(aimed, { x: nx, y: ny }, true);
              chuotLucChon = input.pointer();
            }
          }
          break;
        }

        case "navOk": {
          const el = document.activeElement as HTMLElement | null;
          if (el && el.closest(".modal, .tut-card, #devpanel")) el.click();
          else moveFocus(0, 1); // chưa có gì được chọn: chọn nút đầu tiên
          break;
        }

        case "navBack":
          if (tutorial.isOpen()) tutorial.close();
          else if (menus.isOpen()) menus.close();
          else if (devPanel.isOpen()) devPanel.close();
          break;

        /* ---- NÚT NGỮ CẢNH PHỤ: CHỈ TRA CỨU -----------------------------
           Hai nút, hai câu hỏi, và không nút nào được nuốt câu của nút kia:

             · Nút CHÍNH — "LÀM gì ở đây": cày, gieo, tưới, thu, đổ máng, và
               cả mở cửa hàng / lên giường / múc nước / mở kho.
             · Nút PHỤ   — "cho tôi XEM cái gì đó gần đây": bảng con vật, bảng
               khu chuồng, thẻ ô đang ngắm. Không đổi state, không bao giờ.

           Cường chốt đúng một câu: "một nút ngữ cảnh chính là hành động, một
           nút ngữ cảnh phụ là tra cứu thông tin gần đó." Trước đợt này nút phụ
           gánh cả hai vai, và vai hành động luôn thắng — đứng cạnh quầy thu
           mua thì không còn cách nào xem thẻ ô. `interactHint` giờ chỉ trả về
           thứ ĐỌC ĐƯỢC, nên chỗ này không còn nhánh nào đổi state. */
        case "interact": {
          if (building) {
            // Đang vẽ dở thì B huỷ đoạn; không vẽ gì thì B thoát chế độ.
            if (lineFrom) {
              lineFrom = null;
              lineTo = null;
            } else buildUI.close();
            break;
          }
          if (modal) break;
          /* Cùng một hàm HUD dùng để in nhãn (`infoHint`), nên nút phụ không
             bao giờ ghi một đằng mở một nẻo. */
          const ih = infoHint(s, content, pressCursor(s));
          if (!ih) {
            deny();
            break;
          }
          buzz("tap");
          if (ih.what === "animal" || ih.what === "worker") cardAnimal = cardAnimal === ih.id ? null : ih.id;
          else if (ih.what === "pen") menus.openPen(ih.id);
          else toasts.say(tileInfo(s, content, ih.x, ih.y) ?? "Không có gì ở đây", "info");
          break;
        }
      }
    }

    const s = store.getState();
    if (s.log.length) {
      /* HẾT NĂNG LƯỢNG được phát hiện ở ĐÂY chứ không ở chỗ bấm nút.
         Thao tác có hiệu lực TRỄ: `USE` đặt `busy` ngay rồi mới kiểm năng lượng
         lúc cuốc chạm đất, nên lúc bấm không có cách nào biết nhát này sẽ ăn
         hay trượt. Dòng toast là tín hiệu duy nhất tới đúng lúc — bám vào nó
         thì cái lắc đầu rơi ĐÚNG khoảnh khắc nhát cuốc trượt. */
      const hetSuc = content.strings.msg["noEnergy"];
      if (hetSuc && s.log.some((v) => v.id > logDaLac && v.text === hetSuc)) {
        // Cùng bộ ba như mọi chỗ từ chối khác: tiếng + rung + lắc đầu. Trước
        // đây chỉ có cái lắc đầu — trên điện thoại để trong túi thì bằng không.
        deny();
      }
      logDaLac = s.log[s.log.length - 1]?.id ?? logDaLac;
      const upTo = toasts.show(s.log);
      if (upTo) store.dispatch({ t: "LOG_SEEN", upTo });
    }

    /* Camera LUÔN bám nhân vật. Chấm hết.

       Đợt 7 từng cho camera bám con vật đang mở bảng — nghe hợp lý, và là lỗi
       Cường gặp "ngay từ lúc mở game": `cardAnimal` được đặt bởi những cú chạm
       không hề định mở bảng, rồi không gì xoá nó khi nhân vật đi, nên camera
       theo con bò còn nhân vật đi ra khỏi khung. Muốn nhìn con vật thì đi tới
       — đó cũng là cách người chơi đang mong. */
    camera.follow(s.player.x, s.player.y, dt);
    const navT = nav.target();
    /* CON TRỎ Ô nói ĐÚNG MỘT câu: "tôi sẽ ĐI đâu".
       Đợt 27 tách nó khỏi câu thứ hai ("nút sẽ làm ở đâu") — câu ấy giờ là của
       mũi tên đỏ. Nên nó chỉ hiện khi có một đích DI CHUYỂN thật: chuyến đang
       đi, hoặc ô dưới con trỏ chuột trên máy tính (bấm là đi tới đó).
       Trên cảm ứng lúc đứng yên thì KHÔNG có con trỏ nào cả — trước khi tách,
       nó bám theo ô ngắm nên vẽ đúp với mũi tên, và hai dấu chồng lên nhau ở
       cùng một ô thì cả hai cùng hết nghĩa. */
    const chuotO = modal || input.stickActive() ? null : input.pointer();
    const cursor: Cursor | null = modal
      ? null
      : navT
        ? { x: navT.tx, y: navT.ty, ok: true }
        : chuotO
          ? {
              x: Math.max(0, Math.min(s.w - 1, Math.floor(chuotO.x / TILE))),
              y: Math.max(0, Math.min(s.h - 1, Math.floor(chuotO.y / TILE))),
              ok: true,
            }
          : null;

    // Chuyển ngày: giữ đen 0,35s rồi mở sáng trong 0,9s.
    let fade = 0;
    if (dayFadeAt > 0) {
      const t = elapsed - dayFadeAt;
      fade = t < 0.35 ? 1 : Math.max(0, 1 - (t - 0.35) / 0.9);
      if (fade <= 0) dayFadeAt = 0;
    }

    /* Kéo tuyến CHỈ có trong chế độ xây dựng — mọi công trình đều đi qua đó. */
    const dragId = buildUI.isOpen() ? buildUI.picked() : null;
    if (!buildUI.isOpen() && padCursor) padCursor = null;
    setLineMode(dragId !== null);
    buildUI.update(s, content);

    // Xem trước tuyến: từ ô ấn xuống tới ô đang rê tới.
    let lineCells: { x: number; y: number; ok: boolean }[] | null = null;
    const dich = lineTo ?? padCursor ?? cursor;
    if (dragId && lineFrom && dich) {
      lineCells = linePath(lineFrom.x, lineFrom.y, dich.x, dich.y).map((c) => ({
        x: c.x,
        y: c.y,
        ok: canPlaceBuilding(s, content, dragId, c.x, c.y),
      }));
    }

    /* Nhãn hai nút là HÌNH CHIẾU của đúng cú bấm sẽ xảy ra — cùng `pressPlan`
       / `infoHint`, cùng ô ngắm (`pressCursor`), cùng tuỳ chọn (`pressOpts`).
       Tính TRƯỚC khi vẽ, vì ô nút sẽ tác động (`hint.at`) chính là chỗ MŨI TÊN
       ĐỎ cắm xuống — người chơi nhìn màn hình là biết nút to kia nhắm vào đâu. */
    const oNham = modal ? null : pressCursor(s);
    const hint: Hint | null = modal ? null : hintOf(pressPlan(s, content, oNham, pressOpts()));
    const iHint = modal ? null : infoHint(s, content, oNham);
    /* KHÔNG lọc theo "khác ô con trỏ" nữa: con trỏ nói "tôi sẽ đi đây", mũi tên
       nói "nút sẽ nhắm vào đây" — hai câu khác nhau, trùng ô vẫn phải hiện cả hai.

       Và khi nút KHÔNG làm được gì thì mũi tên vẫn ở đó, chỉ mờ đi: Cường nói
       nó chỉ vào thứ "sắp nhắm tới HOẶC ĐANG NHẮM TỚI". Tắt nó đúng lúc việc
       chưa làm được là bỏ người chơi lại giữa câu hỏi "tôi đang chỉ vào cái
       gì mà bấm không ăn" — mà đó chính là lúc họ cần câu trả lời nhất. */
    const aimArrow = hint?.at ?? oNham;

    const wxDef = weatherDef(s, content);
    const fogUntil = wxDef.fogUntil ?? 0;
    renderer.draw(s, content, cursor, elapsed, {
      lineCells,
      navTarget: navT ? { x: navT.tx, y: navT.ty } : null,
      aimArrow,
      aimArrowOk: !!hint?.at,
      fade,
      reduceMotion: document.body.dataset["motion"] === "reduce",
      weather: {
        wind: wxDef.wind,
        rain: wxDef.wet,
        storm: !!wxDef.storm,
        overcast: !wxDef.wet && wxDef.growMul >= 1 && wxDef.wind >= 0.4 && !wxDef.hot,
        hot: !!wxDef.hot,
        // sương tan dần trong 60 phút cuối trước mốc fogUntil
        fog: fogUntil > 0 && s.minutes < fogUntil ? Math.min(1, (fogUntil - s.minutes) / 60) : 0,
        outdoor: isOutdoor(content, s.mapId),
        season: seasonIndex(s.day, content),
        seasonTint: currentSeason(s, content)?.tint ?? null,
      },
    });

    /* `dangLam` = dải "đang làm gì" của chuyến. Nút chính thôi nhận chuyến từ
       Đợt 34 nên không còn gì để nói ở đó; giữ tham số để HUD không phải đổi
       chữ ký cho một lần bỏ. */
    hud.update(s, content, hint, iHint, null);

    /* Bảng vật nuôi: CHỈ con người chơi đã chạm vào. Tự đóng khi con đó không
       còn (bán thịt, chết đói, sang bản đồ khác) — giữ lại một cái bảng nói về
       con vật không tồn tại là kiểu nói dối khó chịu nhất. */
    const shown =
      modal || cardAnimal === null
        ? null
        : (s.entities.find(
            (e) =>
              e.id === cardAnimal &&
              e.map === s.mapId &&
              (e.kind === "animal" || e.kind === "worker"),
          ) ?? null);
    const the = shown ? (shown.kind === "worker" ? workerCard(shown, content) : animalStats(shown, content)) : null;
    // Thẻ không vẽ ra được (con vật không có định nghĩa…) thì cũng KHÔNG giữ
    // id lại — giữ là một bảng vô hình không có nút ×, không đóng được.
    if (cardAnimal !== null && !the) cardAnimal = null;
    hud.showAnimal(the);
    drawPadBar(menus.isOpen() || devPanel.isOpen(), buildUI.isOpen(), tutorial.isOpen());
    /* ---- GIỮ TIÊU ĐIỂM & CHỖ CUỘN QUA MỖI LẦN VẼ LẠI --------------------
       Mỗi cú bấm trong menu gọi lại `open*()`, mà `shell()` mở đầu bằng
       `root.innerHTML = ""` — nên phần tử đang đeo vòng vàng, chỗ cuộn của
       danh sách, VÀ hoạt cảnh mở sheet đều bị dựng mới. Ba thứ, không phải một:

         · tiêu điểm — bấm "+" ở quầy bán một cái là bị ném về đầu danh sách;
         · chỗ cuộn — mua một thẻ ở cuối lưới bốn mươi thẻ là bị kéo về đầu
           lưới, và cái này ăn cả CHUỘT lẫn NGÓN TAY chứ không riêng tay cầm;
         · hoạt cảnh — cả tấm menu nảy lên mỗi lần bấm, và trong lúc nảy thì
           mọi phép đo toạ độ màn hình đều sai.

       Nhận lại bằng CHỖ NGỒI chứ không bằng định danh: menu này không có id
       ổn định, nhưng nó dựng lại đúng cái bố cục cũ. Xem `src/ui/focus.ts`.

       KHÔNG rào sau `padOn`: chỗ cuộn là lỗi của mọi người dùng, và khôi phục
       tiêu điểm còn chữa luôn việc bấm một nút xong thứ tự phím Tab quay về
       đầu tài liệu. Chỉ nhánh "đặt tiêu điểm ban đầu" mới cần tay cầm — không
       có tay cầm mà tự dưng focus một cái nút là hành vi lạ. */
    /* Mỗi khung hình chỉ HỎI DOM khi có lớp phủ đang mở — sáu `querySelector`
       một khung cho một câu hỏi mà câu trả lời luôn là "không có gì" trong 95%
       thời gian chơi. */
    const r0 = modal || cardAnimal !== null || buildUI.isOpen() ? focusRoot() : null;
    if (r0 && r0 !== focusedRootEl) {
      focusedRootEl = r0;
      const k = khoaManHinh(r0);
      const memo = focusMemo;
      const cungSheet = !!memo && memo.screen === k.screen;
      const veLai = !!memo && cungSheet && memo.tab === k.tab;

      const bodyEl = r0.querySelector<HTMLElement>(".body");
      if (veLai && bodyEl && memo.scroll > 0) bodyEl.scrollTop = memo.scroll;

      /* Cùng một màn vẽ lại thì đừng diễn lại hoạt cảnh MỞ. Đặt được ở đây vì
         khối này chạy trong cùng khung hình với lần dựng lại — trình duyệt
         chưa kịp vẽ khung nào. */
      if (veLai && r0.classList.contains("modal")) r0.style.animation = "none";

      let dich: HTMLElement | null = null;
      if (veLai && memo.at) {
        const dsach = ungVien(r0);
        const i = timChoNgoi(
          dsach.map((el) => choNgoi(el, r0)),
          memo.at,
        );
        dich = i >= 0 ? dsach[i]! : null;
      } else if (cungSheet) {
        /* Chỉ đổi TAB: danh sách mới hoàn toàn, giữ chỗ ngồi cũ chỉ là ném
           vòng vàng vào giữa một danh sách chẳng liên quan. Đặt lên chính cái
           tab vừa chọn để bấm vai tiếp là đi tiếp được ngay. Cách này đúng cho
           cả khi người chơi BẤM CHUỘT vào tab, nên không cần cờ riêng. */
        dich = r0.querySelector<HTMLElement>(".tabs button.on");
      }

      if (dich) focusIn(dich, true);
      else if (padOn) {
        /* Menu vừa MỞ bằng tay cầm mà không có gì đeo vòng vàng thì người chơi
           gạt cần một cái mới thấy nó "bật lên" — nửa giây tưởng máy treo.
           Đặt tiêu điểm vào nút đầu tiên TRONG THÂN, không phải nút ✕ ở tiêu
           đề: nút đầu tiên theo DOM chính là ✕, mà mở menu ra thấy vòng vàng
           nằm trên nút đóng thì bấm "chọn" theo phản xạ là đóng luôn cái vừa mở. */
        const first =
          r0.querySelector<HTMLElement>(
            ".body button:not([disabled]), .body [role='button'], .dev-grid button:not([disabled])",
          ) ?? r0.querySelector<HTMLElement>("button:not([disabled])");
        if (first) focusIn(first);
      }

      // Nút ✕ đeo tên nút HUỶ của tay cầm: người chơi thấy ngay là bấm nút đó
      // cũng đóng được, khỏi phải gạt cần lên tận tiêu đề.
      const x = r0.querySelector<HTMLElement>("[data-x]");
      if (x && padOn) x.dataset["pad"] = padButtonName(input.padInfo(), 1);
    } else if (!r0) {
      focusedRootEl = null;
      focusMemo = null;
    }
    /* GHI NHỚ tiêu điểm: chỉ khi tiêu điểm hoặc chỗ cuộn ĐỔI, không phải mỗi
       khung. `nhoTieuDiem` → `choNgoi` đọc `offsetLeft/offsetTop` dọc chuỗi
       `offsetParent`, tức ép trình duyệt tính layout đồng bộ — 60 lần một giây
       khi menu mở, cho một thứ đứng yên. */
    if (r0) {
      const ae = document.activeElement;
      const sc = r0.querySelector<HTMLElement>(".body")?.scrollTop ?? 0;
      if (ae !== nhoAe || sc !== nhoScroll || r0 !== nhoRoot) {
        nhoAe = ae;
        nhoScroll = sc;
        nhoRoot = r0;
        nhoTieuDiem(r0);
      }
    }

    minimap.setView(camera.rx / TILE, camera.ry / TILE, vpTiles().w, vpTiles().h);
    minimap.update(s, content);
    devPanel.update(s, content);
  }, { onError: khiLoiKhungHinh });

  bootEl.classList.add("done");
  window.setTimeout(() => {
    bootEl.style.display = "none";
  }, 420);
  loop.start();

  // Hướng dẫn lần đầu: chỉ khi chưa xem và đang là ván mới (save cũ = đã biết chơi).
  if (!settings.tutorialSeen && !tiepTucSave) {
    window.setTimeout(() => tutorial.start(buocHuongDan()), 500);
  } else if (!settings.tutorialSeen) setSetting("tutorialSeen", true);

  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>)["__PF"] = {
      store,
      content,
      atlas,
      menus,
      renderer,
      camera,
      settings: () => settings,
      setSetting,
      tutorial,
      step: (dt = 1 / 60, times = 1) => {
        for (let i = 0; i < times; i++) loop.step(dt);
      },
      /**
       * ĐO: chạy `n` khung rồi trả về thống kê.
       *
       * `scripts/bench.mjs` cố ý không đo lớp vẽ (canvas trong Node chỉ đo được
       * phần CPU của mình, không đo được phần trình duyệt thật sự tốn), nên đây
       * là chỗ DUY NHẤT trả lời được câu "một khung hình tốn bao nhiêu".
       *
       * `nenVe` là tổng số khung phải dựng lại cache lớp nền: trên nông trại đã
       * gieo, trước Đợt 28 nó bằng đúng `n`.
       */
      do: (n = 300, dt = 1 / 60) => {
        const t: number[] = [];
        let ve = 0;
        let veMs = 0;
        let li = 0;
        let hinh = 0;
        let o = 0;
        let cat = 0;
        let mon = 0;
        for (let i = 0; i < n; i++) {
          const a0 = performance.now();
          loop.step(dt);
          t.push(performance.now() - a0);
          const st = renderer.stats();
          ve += st.nenVe;
          veMs += st.ms;
          li += st.lat;
          hinh += st.drawImage;
          o += st.fillRect;
          cat += st.culled;
          mon += st.items;
        }
        t.sort((a, b) => a - b);
        const vp = camera.viewport;
        const lam = (v: number) => Math.round(v * 100) / 100;
        return {
          khung: n,
          nenVe: ve,
          "khung p50": lam(t[n >> 1] ?? 0),
          "khung p99": lam(t[Math.min(n - 1, Math.floor(n * 0.99))] ?? 0),
          "draw ms tb": lam(veMs / n),
          "drawImage tb": Math.round(hinh / n),
          "fillRect tb": Math.round(o / n),
          "items tb": Math.round(mon / n),
          "culled tb": Math.round(cat / n),
          "lat tb": Math.round(li / n),
          scale: vp.scale,
          dpr: vp.dpr,
          "px thiết bị/pixel ảnh": (vp.scale * vp.dpr) / ART,
          "backing Mpx": lam((vp.cssW * vp.dpr * (vp.cssH * vp.dpr)) / 1e6),
        };
      },
    };
  }

  for (const w of contentWarnings) toasts.say(w, "bad");

  /* ---- 11. OTA đã hỏi xong ở CỔNG KHỞI ĐỘNG (mục 1b) ----

     Chỗ này từng gọi `checkForUpdate` lần nữa, chạy ngầm. Bỏ đi: hỏi hai lần
     mỗi phiên là tải `latest.json` hai lần cho cùng một câu trả lời, và cái
     toast "có bản cập nhật" mà nó bắn ra nay luôn sai — bản mới đã được áp
     dụng trước khi ván bắt đầu rồi.

     `pendingContentVersion` thì vẫn hỏi: nó đọc cache CHỨ KHÔNG gọi mạng, và
     nó là thứ duy nhất còn nói đúng — pack đã tải nhưng chưa hợp core hiện
     tại thì vẫn nằm chờ, và menu phải nói được điều đó. */
  pendingVersion = await pendingContentVersion(content.contentVersion);
}

/* ---------------------------------------------------------------------------
   CẬP NHẬT PWA

   `registerType: "prompt"` nghĩa là service worker mới KHÔNG tự chiếm quyền.
   Lý do: người chơi đang giữa một ngày trong game mà trang tự tải lại thì mất
   phần chưa lưu. Thay vào đó hiện một thanh nhỏ, bấm mới tải.

   Trước đây bản viết tay gọi `skipWaiting()` ngay lúc cài, nên bản mới lặng lẽ
   thay bản cũ mà không ai biết — và người chơi mở PWA suốt thì ở lại bản cũ vô
   thời hạn vì trang không bao giờ được tải lại.
--------------------------------------------------------------------------- */
if (import.meta.env.PROD) {
  void (async () => {
    const bar = document.querySelector<HTMLElement>("#update-bar");
    const { registerSW } = await import("virtual:pwa-register");
    const update = registerSW({
      immediate: true,
      onNeedRefresh() {
        if (!bar) return;
        bar.hidden = false;
        bar.querySelector<HTMLElement>(".go")?.addEventListener(
          "click",
          () => {
            bar.hidden = true;
            void update(true);
          },
          { once: true },
        );
        bar.querySelector<HTMLElement>(".x")?.addEventListener(
          "click",
          () => {
            bar.hidden = true;
          },
          { once: true },
        );
      },
      onRegisteredSW(_url, reg) {
        /* Dọn cache của service worker VIẾT TAY đời trước. Workbox chỉ tự dọn
           precache của chính nó, nên cái cũ sẽ nằm lại trên máy người chơi mãi
           — không được đọc nữa, chỉ chiếm chỗ. */
        void caches
          .keys()
          .then((ks) =>
            Promise.all(ks.filter((k) => k.startsWith("oni-farm-")).map((k) => caches.delete(k))),
          )
          .catch(() => {});
        if (!reg) return;
        /* Hỏi lại mỗi 30 phút. PWA mở suốt ngày thì không có lần "mở lại trang"
           nào để phát hiện bản mới, nên phải chủ động hỏi. */
        setInterval(() => void reg.update().catch(() => {}), 30 * 60 * 1000);

        /* …và hỏi NGAY khi người chơi quay lại tab.
           Nhịp 30 phút là nhịp cho một tab đang chạy liên tục, không phải cho
           cách người ta thật sự dùng: mở game, chuyển sang việc khác, một lúc
           sau quay lại. Lúc quay lại là đúng lúc đáng hỏi nhất — vừa có khả
           năng cao là đã có bản mới, vừa là khoảnh khắc người chơi sẵn sàng
           nhận nó. Không có nhánh này thì tệ nhất phải chờ trọn 30 phút cho
           một việc đáng lẽ tốn vài trăm mili giây.

           Van tiết: một lần mỗi phút. Chuyển tab qua lại là chuyện xảy ra hàng
           chục lần một buổi, và mỗi lần hỏi là một lượt gọi mạng thật. */
        let hoiLanCuoi = 0;
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState !== "visible") return;
          const gio = Date.now();
          if (gio - hoiLanCuoi < 60_000) return;
          hoiLanCuoi = gio;
          void reg.update().catch(() => {});
        });
      },
    });
  })();
}

/** Khoá CHỐNG LẶP cho việc nhận bản mã mới ở màn khởi động.
 *
 *  Nằm ở `sessionStorage` chứ không `localStorage`: nó chỉ cần sống đúng một
 *  phiên tab. Không có khoá này thì một service worker cài hỏng — nhận quyền
 *  xong vẫn báo `waiting` — sẽ làm trang tự tải lại vô hạn, và người chơi
 *  không vào được game bằng bất cứ cách nào. */
const KHOA_NHAN_SW = "oni-farm:da-nhan-ban-ma";

/**
 * Nhận service worker đang XẾP HÀNG, ngay tại màn khởi động.
 *
 * Trả `true` nghĩa là trang sắp tải lại — người gọi phải dừng khởi động ngay,
 * đừng dựng tiếp một thứ sắp bị vứt đi.
 */
async function nhanBanMaMoi(bootSay: (s: string) => void): Promise<boolean> {
  if (!import.meta.env.PROD) return false;
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (!reg) return false;
    if (!reg.waiting) {
      // Không có gì xếp hàng — hỏi ngầm cho lần mở sau, KHÔNG chờ.
      void reg.update().catch(() => {});
      return false;
    }
    if (sessionStorage.getItem(KHOA_NHAN_SW)) return false;
    sessionStorage.setItem(KHOA_NHAN_SW, "1");
    bootSay("Có bản game mới — đang nhận…");
    reg.waiting.postMessage({ type: "SKIP_WAITING" });
    /* Chờ ĐỔI QUYỀN, nhưng có trần. `controllerchange` là tín hiệu đúng; cái
       hẹn giờ là để một service worker ương ngạnh không giam người chơi ở màn
       khởi động — quá hạn thì cứ tải lại, cùng lắm là vẫn bản cũ. */
    await new Promise<void>((xong) => {
      const t = setTimeout(xong, 3000);
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => {
          clearTimeout(t);
          xong();
        },
        { once: true },
      );
    });
    location.reload();
    return true;
  } catch {
    return false; // không có service worker, hoặc trình duyệt chặn — chơi tiếp
  }
}

void boot().catch((e) => {
  console.error(e);
  const el = document.querySelector("#boot");
  if (el instanceof HTMLElement) {
    el.style.display = "grid";
    el.classList.remove("done");
    el.innerHTML = `<div class="inner"><div class="logo">ONI<span>FARM</span></div><p>Lỗi khởi động:</p>
      <div class="err"></div></div>`;
    (el.querySelector(".err") as HTMLElement).textContent = String(
      e instanceof Error ? (e.stack ?? e.message) : e,
    );
  }
});

/**
 * BUỘC cập nhật.
 *
 * Cố ý mạnh tay: gỡ hẳn service worker và xoá SẠCH mọi cache rồi tải lại.
 * Đường "lịch sự" (`update(true)` của thanh báo) chỉ chạy khi Workbox đã thấy
 * một bản mới đang chờ — mà đúng cái kẹt cần thoát ra là lúc nó KHÔNG thấy:
 * người chơi mở PWA suốt, service worker cũ phục vụ mãi một bản cũ, và không
 * có nút nào thoát ra được.
 *
 * Save nằm ở IndexedDB nên không đụng tới — chỉ cache bị xoá. Đứng ngoài
 * `boot()` vì màn chặn "save của bản mới hơn" cần nó TRƯỚC khi có store.
 */
async function buocCapNhat(): Promise<void> {
  try {
    const regs = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(regs.map((r) => r.unregister()));
  } catch {
    /* bỏ qua */
  }
  try {
    const ks = await caches.keys();
    await Promise.all(ks.map((k) => caches.delete(k)));
  } catch {
    /* bỏ qua */
  }
  // `?v=` để chắc chắn không ăn lại bản trong bộ nhớ đệm của chính trình duyệt.
  location.replace(`${location.pathname}?v=${Date.now()}`);
}
